/* Implements Warm Academic Theme Option 2 configuration & deployment */
import React, { useState, useEffect } from 'react';
import { SponsoredService } from '../../services/sponsoredService';
import { PageLoader } from '../../components/PageLoader';
import { AdConfig, AdType, AdSource } from '../../types';
import { 
    Save, Megaphone, Type, Image as ImageIcon, Video, Link, 
    ExternalLink, Sparkles, CheckCircle,
    Layout, Globe, Smartphone, Code, ShieldAlert,
    Zap, Eye, RefreshCw, Layers, Timer, Clock, Lock,
    Fingerprint, Shield, Activity, HardDrive, 
    Info, Wand2, ShieldCheck, Command, Binary, Play,
    Monitor, Phone, Target, X, ChevronRight, Settings, UserCircle,
    Facebook, Search, MousePointer2, AlertTriangle, FileCode, Loader2,
    Mic2, ListChecks, Bell, History, CalendarDays, Bot, Database,
    Sun, BarChart3, CreditCard, LayoutTemplate, Calculator, SmartphoneNfc, LayoutDashboard,
    Route, ListOrdered, MonitorDot, Tablet,
    Terminal,
    FileSearch,
    ShieldQuestion,
    FileCode2,
    FileText,
    BookOpen,
    Headphones, Plus, Trash2, Edit3, Upload, ArrowUp, ArrowDown, RotateCcw
} from 'lucide-react';
import { AdUnit } from '../../components/AdUnit';
import { DEFAULT_NURSING_CONCEPT_TRACKS } from '../../data/audioTracksData';
import { ConceptAudioTrack } from '../../types';

export const AdManager: React.FC = () => {
    const [localAdConfig, setLocalAdConfig] = useState<AdConfig>(SponsoredService.getConfig());
    const [isSaved, setIsSaved] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [status, setStatus] = useState<{type: 'error' | 'success', text: string} | null>(null);
    const [activeTab, setActiveTab] = useState<'inventory' | 'identity' | 'creative' | 'network' | 'monetization' | 'governance' | 'compliance' | 'themes' | 'mini_player'>('inventory');
    const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
    const [activePlacement, setActivePlacement] = useState<keyof AdConfig['placements']>('dashboard');
    const [placementSearch, setPlacementSearch] = useState('');
    const [placementCategoryFilter, setPlacementCategoryFilter] = useState<'all' | 'quiz' | 'dashboard' | 'study' | 'audio' | 'overlay'>('all');
    
    // Mini Player Track Management State
    const [isTrackModalOpen, setIsTrackModalOpen] = useState(false);
    const [trackEditing, setTrackEditing] = useState<ConceptAudioTrack | null>(null);
    const [uploadingField, setUploadingField] = useState<'img' | 'source' | null>(null);
    const [trackForm, setTrackForm] = useState<ConceptAudioTrack>({
        id: '',
        title: '',
        conceptCategory: 'Clinical & Vitals',
        duration: '3:30',
        img: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=300&auto=format&fit=crop&q=80',
        source: '',
        script: '',
        highYieldPoints: ['']
    });

    const getTracks = (): ConceptAudioTrack[] => {
        if (localAdConfig.miniPlayerTracks && Array.isArray(localAdConfig.miniPlayerTracks) && localAdConfig.miniPlayerTracks.length > 0) {
            return localAdConfig.miniPlayerTracks;
        }
        return DEFAULT_NURSING_CONCEPT_TRACKS;
    };

    const handleOpenAddTrack = () => {
        setTrackEditing(null);
        setTrackForm({
            id: `concept-custom-${Date.now()}`,
            title: '',
            conceptCategory: 'Clinical & Vitals',
            duration: '3:30',
            img: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=300&auto=format&fit=crop&q=80',
            source: '',
            script: '',
            highYieldPoints: ['']
        });
        setIsTrackModalOpen(true);
    };

    const handleOpenEditTrack = (track: ConceptAudioTrack) => {
        setTrackEditing(track);
        setTrackForm({
            ...track,
            highYieldPoints: track.highYieldPoints && track.highYieldPoints.length > 0 ? [...track.highYieldPoints] : ['']
        });
        setIsTrackModalOpen(true);
    };

    const handleSaveTrack = () => {
        if (!trackForm.title.trim() || !trackForm.source.trim()) return;
        const currentList = [...getTracks()];
        const cleanPoints = trackForm.highYieldPoints.filter(p => p.trim() !== '');

        let updatedList: ConceptAudioTrack[];
        if (trackEditing) {
            updatedList = currentList.map(t => t.id === trackEditing.id ? { ...trackForm, highYieldPoints: cleanPoints } : t);
        } else {
            updatedList = [{ ...trackForm, highYieldPoints: cleanPoints }, ...currentList];
        }

        setLocalAdConfig(prev => ({
            ...prev,
            miniPlayerTracks: updatedList
        }));
        setIsTrackModalOpen(false);
    };

    const handleDeleteTrack = (id: string) => {
        const updatedList = getTracks().filter(t => t.id !== id);
        setLocalAdConfig(prev => ({
            ...prev,
            miniPlayerTracks: updatedList
        }));
    };

    const handleMoveTrack = (index: number, direction: 'up' | 'down') => {
        const list = [...getTracks()];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= list.length) return;
        const temp = list[index];
        list[index] = list[targetIndex];
        list[targetIndex] = temp;
        setLocalAdConfig(prev => ({
            ...prev,
            miniPlayerTracks: list
        }));
    };

    const handleResetTracks = () => {
        if (window.confirm("Reset audio summary tracks to standard default nursing concepts?")) {
            setLocalAdConfig(prev => ({
                ...prev,
                miniPlayerTracks: DEFAULT_NURSING_CONCEPT_TRACKS
            }));
        }
    };

    const handleTrackFileUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'img' | 'source') => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingField(field);
        const reader = new FileReader();
        reader.onload = (event) => {
            const result = event.target?.result as string;
            if (field === 'source') {
                const audio = new Audio();
                audio.src = result;
                audio.onloadedmetadata = () => {
                    if (!isNaN(audio.duration) && audio.duration > 0) {
                        const m = Math.floor(audio.duration / 60);
                        const s = Math.floor(audio.duration % 60);
                        setTrackForm(prev => ({ ...prev, source: result, duration: `${m}:${s.toString().padStart(2, '0')}` }));
                    } else {
                        setTrackForm(prev => ({ ...prev, source: result }));
                    }
                    setUploadingField(null);
                };
                audio.onerror = () => {
                    setTrackForm(prev => ({ ...prev, source: result }));
                    setUploadingField(null);
                };
            } else {
                setTrackForm(prev => ({ ...prev, img: result }));
                setUploadingField(null);
            }
        };
        reader.readAsDataURL(file);
    };
    
    // Key used to force-refresh the AdUnit preview when snippet changes
    const [previewKey, setPreviewKey] = useState(Date.now());

    const [diagnosticScanning, setDiagnosticScanning] = useState(false);
    const [diagnosticResult, setDiagnosticResult] = useState<{
        status: 'idle' | 'success' | 'warning' | 'error';
        message: string;
        foundIds: string[];
    }>({ status: 'idle', message: '', foundIds: [] });

    const runTagDiagnostics = () => {
        setDiagnosticScanning(true);
        setTimeout(() => {
            const scriptContent = localAdConfig.googleAdsScript || '';
            const gTagsMatches = scriptContent.match(/(G-[A-Za-z0-9]+|AW-[A-Za-z0-9]+|UA-[A-Za-z0-9-]+)/g) || [];
            const pubIdMatches = scriptContent.match(/(ca-pub-[0-9]+)/gi) || [];
            const parsedIds = Array.from(new Set([...gTagsMatches, ...pubIdMatches]));

            if (!scriptContent.trim()) {
                setDiagnosticResult({
                    status: 'warning',
                    message: "No tracking script SDK configuration found in primary buffer.",
                    foundIds: []
                });
                setDiagnosticScanning(false);
                return;
            }

            // Look inside DOM for parsed IDs
            const docHtml = document.documentElement.innerHTML || '';
            const isPresentInDoc = parsedIds.some(id => docHtml.includes(id)) || 
                                    docHtml.includes('googletagmanager.com') || 
                                    docHtml.includes('adsbygoogle') ||
                                    scriptContent.split('\n').some(line => line.trim() && docHtml.includes(line.trim()));

            if (isPresentInDoc) {
                setDiagnosticResult({
                    status: 'success',
                    message: "G-Tag integration successfully detected in system DOM! The SDK script payload is verified to be live and operating on this host path.",
                    foundIds: parsedIds
                });
            } else {
                setDiagnosticResult({
                    status: 'warning',
                    message: "Configuration saved in registry, but not yet evaluated in this tab. Click Save to backend, then reload (Ctrl+F5 or Refresh) to force server-side injection into the DOM.",
                    foundIds: parsedIds
                });
            }
            setDiagnosticScanning(false);
        }, 1200);
    };

    useEffect(() => {
        const sync = async () => {
            setIsLoading(true);
            try {
                const latest = await SponsoredService.fetchLatestConfig();
                setLocalAdConfig(latest);
            } finally {
                setIsLoading(false);
            }
        };
        sync();
    }, []);

    const handleSave = async () => {
        setIsSyncing(true);
        const updated = await SponsoredService.updateConfig(localAdConfig);
        if (updated) {
            setLocalAdConfig(updated);
        }
        setIsSaved(true);
        setIsSyncing(false);
        setTimeout(() => setIsSaved(false), 3000);
    };

    const handleDeployTheme = async (themeName: any) => {
        setIsSyncing(true);
        const newConfig = { ...localAdConfig, activeTheme: themeName };
        setLocalAdConfig(newConfig);
        const updated = await SponsoredService.updateConfig(newConfig);
        if (updated) {
            setLocalAdConfig(updated);
        }
        setIsSaved(true);
        setIsSyncing(false);
        setTimeout(() => setIsSaved(false), 3000);
    };

    const togglePlacement = (key: keyof AdConfig['placements']) => {
        setLocalAdConfig({
            ...localAdConfig,
            placements: {
                ...localAdConfig.placements,
                [key]: { ...localAdConfig.placements[key], enabled: !localAdConfig.placements[key].enabled }
            }
        });
    };

    const setPlacementSource = (key: keyof AdConfig['placements'], source: AdSource) => {
        setLocalAdConfig({
            ...localAdConfig,
            placements: {
                ...localAdConfig.placements,
                [key]: { ...localAdConfig.placements[key], source }
            }
        });
    };

    const updateHtmlSnippet = (key: keyof AdConfig['placements'], snippet: string) => {
        setLocalAdConfig({
            ...localAdConfig,
            placements: {
                ...localAdConfig.placements,
                [key]: { ...localAdConfig.placements[key], htmlSnippet: snippet }
            }
        });
        // Force refresh the preview to test script injection logic immediately
        setPreviewKey(Date.now());
    };

    const placements = localAdConfig.placements;
    const placementKeys = Object.keys(placements) as Array<keyof AdConfig['placements']>;

    // Placement Metadata Directory for Super Admin Clarity
    const PLACEMENT_METADATA: Record<keyof AdConfig['placements'], {
        title: string;
        category: 'quiz' | 'dashboard' | 'study' | 'audio' | 'overlay';
        categoryLabel: string;
        description: string;
        locationPath: string;
    }> = {
        inline_quiz: {
            title: 'Practice Quiz: Question & Clinical Safety Ad',
            category: 'quiz',
            categoryLabel: 'Practice Quiz',
            description: 'Shown directly beneath the Clinical Safety Analysis & Explain My Mismatch rationale section during practice quizzes.',
            locationPath: 'Practice Quiz > Clinical Safety Analysis / Question Rationale'
        },
        practice_quiz_top: {
            title: 'Practice Quiz: Top Header Banner',
            category: 'quiz',
            categoryLabel: 'Practice Quiz',
            description: 'Sticky banner rendered across the top header of the active Practice Quiz interface.',
            locationPath: 'Practice Quiz > Top Navigation Header'
        },
        practice_quiz_bottom: {
            title: 'Practice Quiz: Bottom Container Banner',
            category: 'quiz',
            categoryLabel: 'Practice Quiz',
            description: 'Banner anchored at the base of the main Practice Quiz container.',
            locationPath: 'Practice Quiz > Bottom Page Container'
        },
        practice_exam_top: {
            title: 'Practice Exam: Top Header Banner',
            category: 'quiz',
            categoryLabel: 'Practice Exam',
            description: 'Sticky header ad slot rendered at the top of the full timed Practice Exam simulation.',
            locationPath: 'Practice Exam > Top Header'
        },
        practice_exam_bottom: {
            title: 'Practice Exam: Bottom Container Banner',
            category: 'quiz',
            categoryLabel: 'Practice Exam',
            description: 'Anchored ad unit at the footer of the active timed Practice Exam page.',
            locationPath: 'Practice Exam > Bottom Container'
        },
        results: {
            title: 'Exam & Quiz Results Screen',
            category: 'quiz',
            categoryLabel: 'Exams & Quizzes',
            description: 'High-impact monetization unit shown immediately upon completing an exam/quiz on the score summary screen.',
            locationPath: 'Exam & Quiz Results > Score Breakdown Screen'
        },
        missed_bank: {
            title: 'Mistake Vault / Missed Bank Dashboard',
            category: 'quiz',
            categoryLabel: 'Exams & Quizzes',
            description: 'Banner placed inside the Mistake Remediation Vault for students reviewing previously missed questions.',
            locationPath: 'Mistake Vault / Missed Questions Dashboard'
        },
        dashboard: {
            title: 'Session Launcher & Setup Bottom',
            category: 'dashboard',
            categoryLabel: 'Dashboard',
            description: 'Shown at the bottom of the pre-quiz and pre-exam launcher configuration screen.',
            locationPath: 'Exam/Quiz Launcher > Setup Configuration Screen'
        },
        dashboard_bottom: {
            title: 'Main Student Dashboard Bottom',
            category: 'dashboard',
            categoryLabel: 'Dashboard',
            description: 'Banner rendered at the base of the primary student study dashboard across all themes.',
            locationPath: 'Student Dashboard > Bottom Section'
        },
        dashboard_welcome_right: {
            title: 'Dashboard Welcome Right (Square Ad)',
            category: 'dashboard',
            categoryLabel: 'Dashboard',
            description: 'Square ad tile shown to free-tier candidates directly beside the student welcome greeting hero.',
            locationPath: 'Student Dashboard > Hero Welcome Banner (Right Side)'
        },
        profile: {
            title: 'Candidate Profile & Readiness Hub',
            category: 'dashboard',
            categoryLabel: 'Dashboard',
            description: 'Sponsorship unit displayed on the candidate profile and certification readiness screen.',
            locationPath: 'User Profile / Readiness Hub'
        },
        sidebar: {
            title: 'Global Navigation Sidebar',
            category: 'dashboard',
            categoryLabel: 'Dashboard',
            description: 'Side navigation ad unit visible across desktop layouts supporting a vertical sidebar.',
            locationPath: 'Global Layout > Sidebar Navigation Footer'
        },
        footer_mobile: {
            title: 'Mobile View Sticky Bottom Banner',
            category: 'dashboard',
            categoryLabel: 'Mobile',
            description: 'Floating bottom sticky banner rendered for smartphone and compact screen viewports.',
            locationPath: 'Mobile Device View > Sticky Footer'
        },
        study_guide_top: {
            title: 'Study Guide: Top Header Banner',
            category: 'study',
            categoryLabel: 'Study Modules',
            description: 'Top banner inside theoretical review chapters and CNA study modules.',
            locationPath: 'Study Hub > Curriculum Guides Top'
        },
        study_guide_bottom: {
            title: 'Study Guide: Bottom Chapter Banner',
            category: 'study',
            categoryLabel: 'Study Modules',
            description: 'Bottom summary banner shown at the conclusion of each study chapter.',
            locationPath: 'Study Hub > Curriculum Guides Bottom'
        },
        skills_lab_top: {
            title: 'Skills Lab: Top Header Banner',
            category: 'study',
            categoryLabel: 'Clinical Skills',
            description: 'Top banner inside interactive procedural simulations and clinical skill steps.',
            locationPath: 'Skills Lab > Clinical Procedure Top'
        },
        skills_lab_bottom: {
            title: 'Skills Lab: Bottom Simulation Banner',
            category: 'study',
            categoryLabel: 'Clinical Skills',
            description: 'Bottom banner displayed below procedural checklist steps in Skills Lab.',
            locationPath: 'Skills Lab > Clinical Procedure Bottom'
        },
        math_lab_dashboard: {
            title: 'Math Lab: Hub Dashboard Banner',
            category: 'study',
            categoryLabel: 'Math Lab',
            description: 'Banner on the primary Dosage & Medical Math calculations entrance hub.',
            locationPath: 'Math Lab > Calculator & Dosage Hub'
        },
        math_lab_sidebar: {
            title: 'Math Lab: Exercise Sidebar Unit',
            category: 'study',
            categoryLabel: 'Math Lab',
            description: 'Sidebar ad unit during active medical dosage calculation drills.',
            locationPath: 'Math Lab > Practice Exercise Sidebar'
        },
        flashcards_top: {
            title: 'Flashcards: Top Dashboard Banner',
            category: 'study',
            categoryLabel: 'Flashcards',
            description: 'Top header ad banner shown inside the spaced-repetition Flashcards study deck.',
            locationPath: 'Flashcards Hub > Top Header'
        },
        ai_tutor: {
            title: 'AI Clinical Tutor Hub Banner',
            category: 'study',
            categoryLabel: 'AI Tutor',
            description: 'Banner rendered in the AI Clinical Tutor interactive chat workspace.',
            locationPath: 'AI Tutor > Conversation Workspace'
        },
        podcasts_top: {
            title: 'Audio Lessons: Top Header Banner',
            category: 'audio',
            categoryLabel: 'Audio Hub',
            description: 'Top banner displayed in the CNA Clinical Audio Lessons & Podcast library.',
            locationPath: 'Audio Lessons Hub > Top Banner'
        },
        podcasts_sidebar: {
            title: 'Audio Lessons: Playlist Sidebar Unit',
            category: 'audio',
            categoryLabel: 'Audio Hub',
            description: 'Sidebar placement beside the audio lesson playlist directory.',
            locationPath: 'Audio Lessons Hub > Playlist Sidebar'
        },
        podcasts_player_sticky: {
            title: 'Audio Player: Sticky Player Banner',
            category: 'audio',
            categoryLabel: 'Audio Hub',
            description: 'Compact sticky banner attached to the ongoing background mini audio player.',
            locationPath: 'Global Mini Player > Sticky Control Bar'
        },
        overlay: {
            title: 'Jurisdictional Intermission (Full Screen Modal)',
            category: 'overlay',
            categoryLabel: 'Intermissions',
            description: 'Full-screen timed intermission overlay presented to free-tier candidates after study intervals.',
            locationPath: 'Global Intermission > Timed Modal Takeover'
        },
        history_list: {
            title: 'Exam History: Attempts List View',
            category: 'overlay',
            categoryLabel: 'History & Audits',
            description: 'Banner shown on the historical test attempts and telemetry log list.',
            locationPath: 'Exam History > Historical Attempts List'
        },
        history_details: {
            title: 'Exam History: Result Audit Details',
            category: 'overlay',
            categoryLabel: 'History & Audits',
            description: 'Ad unit displayed when reviewing deep question-by-question historical exam audits.',
            locationPath: 'Exam History > Detailed Past Audit View'
        }
    };

    // Filter placement keys based on search and category
    const filteredPlacementKeys = placementKeys.filter(key => {
        const meta = PLACEMENT_METADATA[key];
        const title = meta?.title || (key as string).replace(/_/g, ' ');
        const desc = meta?.description || '';
        const location = meta?.locationPath || '';
        const category = meta?.category || 'dashboard';

        // Category filter check
        if (placementCategoryFilter !== 'all' && category !== placementCategoryFilter) {
            return false;
        }

        // Search query check
        if (placementSearch.trim() !== '') {
            const q = placementSearch.toLowerCase().trim();
            const matchesKey = (key as string).toLowerCase().includes(q);
            const matchesTitle = title.toLowerCase().includes(q);
            const matchesDesc = desc.toLowerCase().includes(q);
            const matchesLoc = location.toLowerCase().includes(q);
            return matchesKey || matchesTitle || matchesDesc || matchesLoc;
        }

        return true;
    });

    const ToggleIcon = ({ enabled, onClick }: { enabled: boolean, onClick: () => void }) => (
        <button 
            onClick={onClick}
            className={`relative w-12 h-6 rounded-full transition-all duration-300 ${enabled ? 'bg-indigo-600' : 'bg-slate-200'}`}
        >
            <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 transform ${enabled ? 'translate-x-6' : 'translate-x-0'} shadow-sm`} />
        </button>
    );

    if (isLoading) {
        return <PageLoader message="Synchronizing Registry Ads..." />;
    }

    return (
        <div className="space-y-6 animate-fade-in pb-24 h-full flex flex-col max-w-[1600px] mx-auto text-slate-900 px-4 md:px-0">
            
            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6">
                <div className="flex-1 bg-slate-950 text-white p-8 md:p-10 rounded-[2.5rem] shadow-2xl relative overflow-hidden group border border-white/10">
                    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-1000 pointer-events-none">
                        <Command size={300} />
                    </div>
                    <div className="relative z-10">
                        <div className="flex flex-wrap items-center gap-3 mb-6">
                            <div className="px-4 py-1.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-black rounded-full border border-emerald-500/30 flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                                Ad Intelligence Node: GA-8
                            </div>
                            {isSyncing && <div className="flex items-center gap-2 text-indigo-400 animate-pulse"><RefreshCw size={12}/> <span className="text-[10px] font-black">Syncing Cloud...</span></div>}
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-none mb-4">System Control</h2>
                        <p className="text-slate-400 font-medium text-sm md:text-lg max-w-2xl leading-relaxed opacity-80">
                            Orchestrate banner placements and jurisdictional intermissions. Configure monetization tiers and platform governance.
                        </p>
                    </div>
                </div>

                <div className="w-full lg:w-96 bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col justify-between">
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={16} className="text-indigo-600" />
                                <span className="text-[10px] font-black text-slate-400 tracking-widest">Master Feed</span>
                            </div>
                            <span className={`text-[9px] font-black px-2 py-0.5 rounded-md ${localAdConfig.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                {localAdConfig.isActive ? 'Network Online' : 'Network Restricted'}
                            </span>
                        </div>
                        
                        <div className="p-6 bg-slate-900 rounded-[2rem] text-white space-y-6 shadow-xl border border-white/5">
                            <div className="flex items-center justify-between">
                                <span className="text-11px font-black text-slate-400 tracking-widest">Kill Switch</span>
                                <ToggleIcon 
                                    enabled={localAdConfig.isActive} 
                                    onClick={() => setLocalAdConfig({...localAdConfig, isActive: !localAdConfig.isActive})} 
                                />
                            </div>
                            <button 
                                onClick={handleSave}
                                disabled={isSaved || isSyncing}
                                className={`w-full py-4 rounded-2xl font-black text-[10px] tracking-widest transition-all flex items-center justify-center gap-3 shadow-2xl active:scale-95 border-b-4 ${isSaved ? 'bg-emerald-600 border-emerald-800 text-white' : 'bg-indigo-600 hover:bg-indigo-50 border-indigo-900 text-white disabled:opacity-50'}`}
                            >
                                {isSaved ? <><CheckCircle size={18}/> Registry Published</> : <><Save size={18}/> Push Updates</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sub-navigation Tabs */}
            <div className="bg-white dark:bg-slate-900 p-2 md:p-2.5 rounded-[1.5rem] md:rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm w-full flex flex-wrap items-center gap-1.5 md:gap-2">
                {[
                    { id: 'inventory', label: 'Placements', icon: Layers },
                    { id: 'creative', label: 'Internal Studio', icon: ImageIcon },
                    { id: 'network', label: 'Tracking & SDKs', icon: FileCode },
                    { id: 'identity', label: 'Features & Guest Limits', icon: Fingerprint },
                    { id: 'mini_player', label: 'Mini Audio Player', icon: Headphones },
                    { id: 'monetization', label: 'Monetization Strategy', icon: CreditCard },
                    { id: 'governance', label: 'Governance', icon: ShieldAlert },
                    { id: 'compliance', label: 'Compliance', icon: ShieldCheck },
                    { id: 'themes', label: 'Theme Management', icon: LayoutTemplate }
                ].map(tab => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button 
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`px-4 xl:px-5 py-2.5 md:py-3 rounded-xl md:rounded-2xl text-[10px] md:text-[11px] font-black tracking-widest uppercase flex items-center gap-2 md:gap-2.5 transition-all duration-200 ${
                                isActive 
                                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-md translate-y-[-1px]' 
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/80 dark:hover:bg-slate-800/60'
                            }`}
                        >
                            <tab.icon size={15} className={isActive ? 'text-indigo-400 dark:text-indigo-600' : 'text-slate-400 dark:text-slate-500'} />
                            <span>{tab.label}</span>
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 flex-1 min-h-0">
                
                {/* LEFT COLUMN: Controls */}
                <div className="xl:col-span-7 space-y-6">
                    {activeTab === 'inventory' && (
                        <div className="space-y-8 animate-slide-up">
                            
                            {/* SEARCH & CATEGORY FILTER TOOLBAR */}
                            <div className="bg-white rounded-[2rem] p-6 border border-slate-200 shadow-sm space-y-4">
                                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                    <div className="relative flex-1 w-full">
                                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input 
                                            type="text" 
                                            value={placementSearch}
                                            onChange={e => setPlacementSearch(e.target.value)}
                                            placeholder="Search placements (e.g., 'clinical safety', 'quiz', 'dashboard', 'inline_quiz')..."
                                            className="w-full pl-11 pr-10 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                                        />
                                        {placementSearch && (
                                            <button 
                                                onClick={() => setPlacementSearch('')}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 self-end md:self-center shrink-0">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                            {filteredPlacementKeys.length} of {placementKeys.length} Slots
                                        </span>
                                    </div>
                                </div>

                                {/* Category Pills */}
                                <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-slate-100">
                                    {[
                                        { id: 'all', label: 'All Placements', count: placementKeys.length },
                                        { id: 'quiz', label: 'Practice & Quizzes', count: placementKeys.filter(k => PLACEMENT_METADATA[k]?.category === 'quiz').length },
                                        { id: 'dashboard', label: 'Dashboards & Hub', count: placementKeys.filter(k => PLACEMENT_METADATA[k]?.category === 'dashboard').length },
                                        { id: 'study', label: 'Study Guides & Labs', count: placementKeys.filter(k => PLACEMENT_METADATA[k]?.category === 'study').length },
                                        { id: 'audio', label: 'Audio Lessons', count: placementKeys.filter(k => PLACEMENT_METADATA[k]?.category === 'audio').length },
                                        { id: 'overlay', label: 'Overlays & History', count: placementKeys.filter(k => PLACEMENT_METADATA[k]?.category === 'overlay').length }
                                    ].map(cat => {
                                        const isSelected = placementCategoryFilter === cat.id;
                                        return (
                                            <button
                                                key={cat.id}
                                                onClick={() => setPlacementCategoryFilter(cat.id as any)}
                                                className={`px-3 py-1.5 rounded-lg text-[10px] font-black tracking-wider uppercase transition-all flex items-center gap-1.5 ${
                                                    isSelected 
                                                        ? 'bg-indigo-600 text-white shadow-sm' 
                                                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                }`}
                                            >
                                                <span>{cat.label}</span>
                                                <span className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold ${isSelected ? 'bg-indigo-700 text-white' : 'bg-slate-200 text-slate-600'}`}>
                                                    {cat.count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* PLACEMENTS GRID */}
                            {filteredPlacementKeys.length === 0 ? (
                                <div className="p-12 text-center bg-white rounded-[2rem] border border-slate-200 space-y-3">
                                    <FileSearch size={36} className="mx-auto text-slate-300" />
                                    <p className="text-sm font-black text-slate-700">No placements matching "{placementSearch}"</p>
                                    <p className="text-xs text-slate-400">Try searching for "quiz", "clinical", "safety", or reset your category filters.</p>
                                    <button 
                                        onClick={() => { setPlacementSearch(''); setPlacementCategoryFilter('all'); }}
                                        className="px-4 py-2 bg-indigo-50 text-indigo-600 text-xs font-black rounded-xl hover:bg-indigo-100 transition-colors"
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {filteredPlacementKeys.map(p => {
                                        const meta = PLACEMENT_METADATA[p] || {
                                            title: (p as string).replace(/_/g, ' '),
                                            category: 'dashboard',
                                            categoryLabel: 'Placement',
                                            description: `Placement: ${p as string}`,
                                            locationPath: `System Node: ${p as string}`
                                        };
                                        const isSelected = activePlacement === p;
                                        const isInlineQuiz = p === 'inline_quiz';

                                        return (
                                            <div 
                                                key={p as string} 
                                                onClick={() => { setActivePlacement(p); setPreviewKey(Date.now()); }}
                                                className={`p-6 rounded-[2rem] border-2 cursor-pointer transition-all group relative overflow-hidden flex flex-col justify-between ${
                                                    isSelected 
                                                        ? 'border-indigo-500 shadow-xl bg-indigo-50/20' 
                                                        : isInlineQuiz 
                                                            ? 'bg-white border-indigo-200 hover:border-indigo-300' 
                                                            : 'bg-white border-slate-100 opacity-80 hover:opacity-100'
                                                }`}
                                            >
                                                {/* Header Category & Key Badge */}
                                                <div className="flex items-center justify-between gap-2 mb-4">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[8px] font-black tracking-widest uppercase rounded-md">
                                                            {meta.categoryLabel}
                                                        </span>
                                                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[8px] font-mono font-black tracking-tight rounded-md border border-indigo-100">
                                                            id: {p as string}
                                                        </span>
                                                        {isInlineQuiz && (
                                                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[8px] font-black uppercase rounded-md tracking-wider">
                                                                Clinical Safety Slot
                                                            </span>
                                                        )}
                                                    </div>
                                                    <ToggleIcon 
                                                        enabled={localAdConfig.placements[p]?.enabled} 
                                                        onClick={() => togglePlacement(p as keyof AdConfig['placements'])} 
                                                    />
                                                </div>

                                                <div className="flex items-start gap-3.5 mb-5">
                                                    <div className={`p-3 rounded-2xl transition-all shadow-sm shrink-0 ${localAdConfig.placements[p]?.enabled ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-400'}`}>
                                                        {p === 'sidebar' ? <Layout size={18} /> : 
                                                         p === 'profile' ? <UserCircle size={18}/> : 
                                                         p === 'footer_mobile' ? <Smartphone size={18} /> :
                                                         p === 'inline_quiz' ? <FileText size={18} /> :
                                                         (p === 'history_list' || p === 'history_details') ? <History size={18} /> :
                                                         p === 'ai_tutor' ? <Bot size={18} /> :
                                                         p === 'missed_bank' ? <Database size={18} /> :
                                                         (p === 'math_lab_dashboard' || p === 'math_lab_sidebar') ? <Calculator size={18} /> :
                                                         (p === 'podcasts_top' || p === 'podcasts_sidebar' || p === 'podcasts_player_sticky') ? <Mic2 size={18} /> :
                                                         p === 'dashboard_bottom' ? <LayoutDashboard size={18} /> :
                                                         p === 'dashboard_welcome_right' ? <LayoutDashboard size={18} /> :
                                                         p === 'flashcards_top' ? <Zap size={18} /> :
                                                         (p === 'study_guide_top' || p === 'study_guide_bottom' || p === 'skills_lab_top' || p === 'skills_lab_bottom') ? <BookOpen size={18} /> :
                                                         (p === 'practice_exam_top' || p === 'practice_exam_bottom') ? <MonitorDot size={18} /> :
                                                         (p === 'practice_quiz_top' || p === 'practice_quiz_bottom') ? <Target size={18} /> :
                                                         p === 'dashboard' ? <LayoutDashboard size={18} /> :
                                                         <Monitor size={18} />}
                                                    </div>
                                                    <div className="min-w-0 space-y-1">
                                                        <h4 className="text-xs font-black tracking-tight text-slate-900 leading-tight">
                                                            {meta.title}
                                                        </h4>
                                                        <p className="text-[9px] text-slate-500 font-medium leading-relaxed">
                                                            {meta.description}
                                                        </p>
                                                        <p className="text-[8px] font-bold text-slate-400 flex items-center gap-1 pt-0.5">
                                                            <Route size={10} className="text-indigo-500" />
                                                            <span>{meta.locationPath}</span>
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Controls row */}
                                                <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2 mt-auto">
                                                    <span className={`text-[8px] font-black tracking-widest uppercase ${localAdConfig.placements[p]?.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                        {localAdConfig.placements[p]?.enabled ? '● Active' : '○ Disabled'}
                                                    </span>

                                                    <div className={`grid grid-cols-2 gap-1.5 transition-opacity ${localAdConfig.placements[p]?.enabled ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); setPlacementSource(p as keyof AdConfig['placements'], 'internal'); }}
                                                            className={`px-3 py-1.5 rounded-lg text-[8px] font-black tracking-widest uppercase border transition-all ${localAdConfig.placements[p]?.source === 'internal' ? 'bg-slate-900 text-white border-slate-900 shadow-xs' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                                                        >
                                                            Internal
                                                        </button>
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); setPlacementSource(p as keyof AdConfig['placements'], 'network'); }}
                                                            className={`px-3 py-1.5 rounded-lg text-[8px] font-black tracking-widest uppercase border transition-all ${localAdConfig.placements[p]?.source === 'network' ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                                                        >
                                                            Display Ads
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* ADVANCED PLACEMENT SETTINGS */}
                            <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm space-y-8 animate-slide-up">
                                <div className="flex items-center gap-4 border-b border-slate-100 pb-6">
                                    <div className="p-3 bg-slate-950 text-white rounded-2xl shadow-xl"><FileCode2 size={24}/></div>
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-xl font-black tracking-tight">{PLACEMENT_METADATA[activePlacement]?.title || (activePlacement as string).replace(/_/g, ' ')}</h3>
                                            <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 text-[9px] font-mono font-black rounded-md border border-indigo-100">
                                                id: {activePlacement as string}
                                            </span>
                                        </div>
                                        <p className="text-slate-500 text-xs font-medium">
                                            {PLACEMENT_METADATA[activePlacement]?.description || 'Custom placement configuration and Display Ad code injection.'}
                                        </p>
                                        <p className="text-[9px] font-black text-indigo-600 flex items-center gap-1.5 pt-1">
                                            <Route size={12} />
                                            <span>Location: {PLACEMENT_METADATA[activePlacement]?.locationPath || activePlacement}</span>
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="space-y-6">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2">
                                            <Code size={14} className="text-indigo-600" /> Display Ad HTML Snippet
                                        </label>
                                        <textarea 
                                            value={localAdConfig.placements[activePlacement]?.htmlSnippet || ''}
                                            onChange={e => updateHtmlSnippet(activePlacement, e.target.value)}
                                            className="w-full p-6 bg-slate-950 text-indigo-400 font-mono text-[11px] rounded-[2rem] h-64 border border-white/10 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all shadow-inner custom-scrollbar"
                                            placeholder="Paste the <ins> and <script> code from Google AdSense here..."
                                        />
                                        <div className="p-4 bg-indigo-50 rounded-2xl flex items-start gap-4">
                                            <Info size={18} className="text-indigo-600 shrink-0 mt-0.5" />
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-black text-indigo-700">Registry Execution Policy</p>
                                                <p className="text-[9px] font-medium text-indigo-900 leading-relaxed opacity-80">
                                                    "If provided, this snippet will override standard auto-filling logic for the {activePlacement} node. The system manually evaluates script tags to ensure compatibility with dynamic DOM rendering."
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'network' && (
                        <div className="space-y-6 animate-slide-up">
                            <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm space-y-10">
                                <div className="flex items-center justify-between border-b border-slate-100 pb-8">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-indigo-600 rounded-2xl shadow-xl text-white"><Binary size={24}/></div>
                                        <div>
                                            <h3 className="text-xl font-black tracking-tight">Script Injection Registry</h3>
                                            <p className="text-slate-400 text-xs font-bold tracking-widest">Manage Google AdSense and Tracking Payloads</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-6">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Globe size={14} className="text-indigo-600" /> AdSense/Analytics SDK</label>
                                        <textarea 
                                            value={localAdConfig.googleAdsScript}
                                            onChange={e => setLocalAdConfig({...localAdConfig, googleAdsScript: e.target.value})}
                                            className="w-full p-6 bg-slate-950 text-emerald-400 font-mono text-[11px] rounded-[2rem] h-48 border border-white/10 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all shadow-inner"
                                            placeholder="Paste <script> tag for AdSense client ID here..."
                                        />
                                        <div className="flex items-center gap-2 p-3 bg-indigo-50 rounded-xl">
                                            <Info size={14} className="text-indigo-600" />
                                            <p className="text-[9px] font-bold text-indigo-700">System will automatically extract the 'ca-pub-xxxx' identifier for Network units.</p>
                                        </div>

                                        {/* DIAGNOSTIC ACTIVE CHECKER BLOCK */}
                                        <div className="p-6 bg-slate-50 border border-slate-200 rounded-3xl space-y-4 shadow-inner">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="text-xs font-black tracking-tight flex items-center gap-2 text-slate-800">
                                                        <Activity size={14} className="text-indigo-600 animate-pulse" /> Active Tag Verification Diagnostic
                                                     </h4>
                                                     <p className="text-[9px] font-bold text-slate-400 tracking-wider">Inspect and verify target script mount in the current browser document.</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={runTagDiagnostics}
                                                    disabled={diagnosticScanning}
                                                    className="px-4 py-2 bg-slate-900 border border-slate-800 hover:bg-slate-950 text-white font-black text-[9px] tracking-wider uppercase rounded-xl flex items-center gap-2 transition-all hover:scale-102 active:scale-98 disabled:opacity-50 shrink-0"
                                                >
                                                    {diagnosticScanning ? (
                                                        <>
                                                            <Loader2 size={12} className="animate-spin text-emerald-400" /> Scanning DOM...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Play size={10} className="text-emerald-400 fill-emerald-400" /> Run Diagnostics
                                                        </>
                                                    )}
                                                </button>
                                            </div>

                                            {diagnosticResult.status !== 'idle' && (
                                                <div className={`p-4 rounded-2xl border flex items-start gap-3 animate-fade-in ${
                                                    diagnosticResult.status === 'success' 
                                                        ? 'bg-emerald-50 border-emerald-200 text-emerald-900' 
                                                        : 'bg-amber-50 border-amber-200 text-amber-900'
                                                }`}>
                                                    <div className="pt-0.5">
                                                        {diagnosticResult.status === 'success' ? (
                                                            <CheckCircle size={16} className="text-emerald-600" />
                                                        ) : (
                                                            <AlertTriangle size={16} className="text-amber-500" />
                                                        )}
                                                    </div>
                                                    <div className="space-y-2 flex-1">
                                                        <p className="text-[10px] font-bold leading-relaxed">{diagnosticResult.message}</p>
                                                        {diagnosticResult.foundIds.length > 0 && (
                                                             <div className="flex flex-wrap items-center gap-1.5 pt-1">
                                                                 <span className="text-[8px] font-black uppercase text-slate-400">Identified Keys:</span>
                                                                 {diagnosticResult.foundIds.map(id => (
                                                                     <code key={id} className="text-[9px] font-black px-1.5 py-0.5 bg-white border border-slate-200 text-indigo-600 rounded">
                                                                         {id}
                                                                     </code>
                                                                 ))}
                                                             </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-slate-100 space-y-6">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-indigo-500/20 text-indigo-600 rounded-xl"><Facebook size={18}/></div>
                                                <div>
                                                    <p className="text-[11px] font-black">Meta Pixel Tracking</p>
                                                    <p className="text-[9px] font-bold text-slate-400 tracking-widest">Retargeting & Conversion Optimization</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[9px] font-black text-slate-400">Dev Mode</span>
                                                    <ToggleIcon 
                                                        enabled={localAdConfig.metaPixelEnableInDev} 
                                                        onClick={() => setLocalAdConfig({...localAdConfig, metaPixelEnableInDev: !localAdConfig.metaPixelEnableInDev})} 
                                                    />
                                                </div>
                                                <ToggleIcon 
                                                    enabled={localAdConfig.metaPixelEnabled} 
                                                    onClick={() => setLocalAdConfig({...localAdConfig, metaPixelEnabled: !localAdConfig.metaPixelEnabled})} 
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Meta Pixel ID</label>
                                            <input 
                                                type="text" 
                                                value={localAdConfig.metaPixelId} 
                                                onChange={e => setLocalAdConfig({...localAdConfig, metaPixelId: e.target.value})} 
                                                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold outline-none focus:bg-white transition-all shadow-inner" 
                                                placeholder="e.g. 123456789012345" 
                                            />
                                        </div>
                                     </div>

                                     <div className="pt-6 border-t border-slate-100 space-y-6">
                                         <div className="flex items-center gap-3">
                                             <div className="p-2 bg-emerald-500/20 text-emerald-600 rounded-xl"><FileText size={18}/></div>
                                             <div>
                                                 <p className="text-[11px] font-black">AI SEO: llms.txt Template</p>
                                                 <p className="text-[9px] font-bold text-slate-400 tracking-widest">Optimize for ChatGPT, Gemini & Claude</p>
                                             </div>
                                         </div>

                                         <div className="space-y-3">
                                             <textarea 
                                                 value={localAdConfig.llmsTxtTemplate}
                                                 onChange={e => setLocalAdConfig({...localAdConfig, llmsTxtTemplate: e.target.value})}
                                                 className="w-full p-6 bg-slate-950 text-emerald-400 font-mono text-[11px] rounded-[2rem] h-64 border border-white/10 outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all shadow-inner"
                                                 placeholder="# {{APP_NAME}}..."
                                             />
                                             <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                                 <div className="space-y-1">
                                                     <p className="text-[9px] font-black text-slate-400">Available Placeholders</p>
                                                     <div className="flex flex-wrap gap-2">
                                                         {['{{APP_NAME}}', '{{DESCRIPTION}}', '{{CERT_TYPES}}', '{{FEATURES}}'].map(p => (
                                                             <code key={p} className="text-[8px] font-bold bg-white px-2 py-1 rounded border border-slate-200 text-indigo-600">{p}</code>
                                                         ))}
                                                     </div>
                                                 </div>
                                                 <div className="flex items-center gap-2">
                                                     <Info size={14} className="text-slate-400" />
                                                     <p className="text-[8px] font-bold text-slate-500 leading-tight">Leave empty to use the system default template. Certifications and features update automatically.</p>
                                                 </div>
                                             </div>
                                         </div>
                                     </div>
                                 </div>
                            </div>

                            {/* ADSENSE DIAGNOSTIC GUIDE */}
                            <div className="bg-slate-900 rounded-[3rem] p-10 text-white relative overflow-hidden border border-white/5">
                                <div className="absolute top-0 right-0 p-8 opacity-5"><ShieldQuestion size={160}/></div>
                                <div className="relative z-10 space-y-8">
                                    <div className="flex items-center gap-4 border-b border-white/5 pb-6">
                                        <div className="p-3 bg-indigo-500/20 text-indigo-400 rounded-2xl border border-indigo-500/30"><Terminal size={24}/></div>
                                        <div>
                                            <h4 className="text-lg font-black tracking-tight">AdSense 400 Diagnostic Guide</h4>
                                            <p className="text-[8px] font-black text-slate-500 tracking-widest">Maintaining Security Protocol Compliance</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-4">
                                            <div className="flex items-start gap-4">
                                                <div className="w-6 h-6 rounded-lg bg-indigo-600 flex items-center justify-center font-black text-[10px] shrink-0 shadow-lg">1</div>
                                                <div className="space-y-1">
                                                    <p className="text-[11px] font-black text-indigo-400">Site Registry Check</p>
                                                    <p className="text-[10px] text-slate-400 leading-relaxed font-medium">Verify that <span className="text-white">study.diamondnursingschool.com</span> is added and "Ready" in the AdSense Dashboard. Under <span className="text-white">Strict Policy</span>, Google relies on the pre-approved domain list.</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-4">
                                                <div className="w-6 h-6 rounded-lg bg-indigo-600 flex items-center justify-center font-black text-[10px] shrink-0 shadow-lg">2</div>
                                                <div className="space-y-1">
                                                    <p className="text-[11px] font-black text-indigo-400">Service Worker Pass-Through</p>
                                                    <p className="text-[10px] text-slate-400 leading-relaxed font-medium">The system now enforces a "Hard Bypass" for Google traffic. Requests are handled natively by the browser to prevent mangled headers.</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="flex items-start gap-4">
                                                <div className="w-6 h-6 rounded-lg bg-indigo-600 flex items-center justify-center font-black text-[10px] shrink-0 shadow-lg">3</div>
                                                <div className="space-y-1">
                                                    <p className="text-[11px] font-black text-indigo-400">Enhanced Tracking</p>
                                                    <p className="text-[10px] text-slate-400 leading-relaxed font-medium">Firefox or Brave "Strict" protection may block ad frames regardless of site policy. Verify logic using Chrome or Safari to check if the browser is blocking ads locally.</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-4">
                                                <div className="w-6 h-6 rounded-lg bg-indigo-600 flex items-center justify-center font-black text-[10px] shrink-0 shadow-lg">4</div>
                                                <div className="space-y-1">
                                                    <p className="text-[11px] font-black text-indigo-400">Ownership Logic</p>
                                                    <p className="text-[10px] text-slate-400 leading-relaxed font-medium">Ensure <span className="text-white">ads.txt</span> is active at the root. Our dispatch layer routes this to the institutional gateway correctly.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'creative' && (
                         <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm space-y-8 animate-slide-up">
                            <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
                                <div className="p-3 bg-medical-600 text-white rounded-2xl shadow-xl"><Wand2 size={24}/></div>
                                <div>
                                    <h3 className="text-xl font-black tracking-tight">Internal Creative Studio</h3>
                                    <p className="text-slate-400 text-xs font-bold tracking-widest">Master assets for non-network advertisements</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Asset Format</label>
                                        <div className="flex bg-slate-50 p-1 rounded-2xl border border-slate-200 shadow-inner">
                                            {['text', 'image', 'video'].map(t => (
                                                <button key={t} onClick={() => setLocalAdConfig({...localAdConfig, type: t as any})} className={`flex-1 py-3 rounded-xl text-[10px] font-black tracking-widest transition-all ${localAdConfig.type === t ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>{t}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Headline Content</label>
                                        <input type="text" value={localAdConfig.title} onChange={e => setLocalAdConfig({...localAdConfig, title: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold outline-none focus:bg-white transition-all shadow-inner" placeholder="Bespoke offer title..." />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Description Narrative</label>
                                        <textarea value={localAdConfig.description} onChange={e => setLocalAdConfig({...localAdConfig, description: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold outline-none focus:bg-white h-24 resize-none" placeholder="Details of the partner offer..." />
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Media Source URL</label>
                                        <div className="relative group">
                                            <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-50 transition-colors" size={18} />
                                            <input type="text" value={localAdConfig.mediaUrl} onChange={e => setLocalAdConfig({...localAdConfig, mediaUrl: e.target.value})} className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-xs outline-none focus:bg-white transition-all" placeholder="https://..." />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Target Landing Page</label>
                                        <div className="relative group">
                                            <ExternalLink className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-50 transition-colors" size={18} />
                                            <input type="text" value={localAdConfig.targetUrl} onChange={e => setLocalAdConfig({...localAdConfig, targetUrl: e.target.value})} className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold outline-none focus:bg-white transition-all" placeholder="https://..." />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Call-To-Action Label</label>
                                        <input type="text" value={localAdConfig.ctaText} onChange={e => setLocalAdConfig({...localAdConfig, ctaText: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-black text-[10px] tracking-widest outline-none focus:bg-white" placeholder="LEARN MORE" />
                                    </div>
                                </div>
                            </div>
                         </div>
                    )}

                    {activeTab === 'identity' && (
                        <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm space-y-10 animate-slide-up">
                            <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
                                <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-xl"><Fingerprint size={24}/></div>
                                <div>
                                    <h3 className="text-xl font-black tracking-tight">Platform Feature Toggles</h3>
                                    <p className="text-slate-400 text-xs font-bold tracking-widest">Enable or disable core system modules</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                                <FeatureToggle label="PWA App Installation" val={localAdConfig.isPwaEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isPwaEnabled: !localAdConfig.isPwaEnabled})} />
                                <FeatureToggle label="Audio Hub (Podcasts)" val={localAdConfig.isPodcastsEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isPodcastsEnabled: !localAdConfig.isPodcastsEnabled})} />
                                <FeatureToggle label="Mini Audio Player (Concept Summaries)" val={localAdConfig.isMiniPlayerEnabled !== false} onToggle={() => setLocalAdConfig({...localAdConfig, isMiniPlayerEnabled: localAdConfig.isMiniPlayerEnabled === false ? true : false})} />
                                <FeatureToggle label="Study Hub AI Search" val={localAdConfig.isRegistrySearchEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isRegistrySearchEnabled: !localAdConfig.isRegistrySearchEnabled})} />
                                <FeatureToggle label="AI Study Planner" val={localAdConfig.isStudyPlannerEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isStudyPlannerEnabled: !localAdConfig.isStudyPlannerEnabled})} />
                                <FeatureToggle label="Clinical Readiness Diagnostic" val={localAdConfig.isDiagnosticEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isDiagnosticEnabled: !localAdConfig.isDiagnosticEnabled})} />
                                <FeatureToggle label="Daily Streaks (Incentives)" val={localAdConfig.isStreaksEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isStreaksEnabled: !localAdConfig.isStreaksEnabled})} />
                                <FeatureToggle label="Weak Point Remediation" val={localAdConfig.isWeakPointRemediationEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isWeakPointRemediationEnabled: !localAdConfig.isWeakPointRemediationEnabled})} />
                                <FeatureToggle label="Social Proof (Activity Feed)" val={localAdConfig.isSocialProofEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isSocialProofEnabled: !localAdConfig.isSocialProofEnabled})} />
                                <FeatureToggle label="Readiness Forecasting" val={localAdConfig.isReadinessForecastEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isReadinessForecastEnabled: !localAdConfig.isReadinessForecastEnabled})} />
                                <FeatureToggle label="Math Lab AI Engine" val={localAdConfig.isMathLabAiEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isMathLabAiEnabled: !localAdConfig.isMathLabAiEnabled})} />
                                <FeatureToggle label="Structured Roadmap (Fixed Tests)" val={localAdConfig.isFixedTestsEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isFixedTestsEnabled: !localAdConfig.isFixedTestsEnabled})} />
                                <FeatureToggle label="Scenario AI Illustrations" val={localAdConfig.enableScenarioAIImages} onToggle={() => setLocalAdConfig({...localAdConfig, enableScenarioAIImages: !localAdConfig.enableScenarioAIImages})} />
                                <FeatureToggle label="AI Distractor Analysis" val={localAdConfig.enableAIRationales} onToggle={() => setLocalAdConfig({...localAdConfig, enableAIRationales: !localAdConfig.enableAIRationales})} />
                                <FeatureToggle label="Ad-Intermission (Free Tier)" val={localAdConfig.intermissionEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, intermissionEnabled: !localAdConfig.intermissionEnabled})} />
                                <FeatureToggle label="Vibrant Visual Themes (Colorful Cards)" val={localAdConfig.enableVibrantCards || false} onToggle={() => setLocalAdConfig({...localAdConfig, enableVibrantCards: !localAdConfig.enableVibrantCards})} />
                                <FeatureToggle label="Advanced Student Analytics" val={localAdConfig.isAnalyticsEnabled || false} onToggle={() => setLocalAdConfig({...localAdConfig, isAnalyticsEnabled: !localAdConfig.isAnalyticsEnabled})} />
                                <FeatureToggle label="8-Question Quick Quiz" val={localAdConfig.isQuickQuizEnabled !== false} onToggle={() => setLocalAdConfig({...localAdConfig, isQuickQuizEnabled: localAdConfig.isQuickQuizEnabled === false ? true : false})} />
                                <FeatureToggle label="Skills Clinical Exam Practice Mode" val={localAdConfig.isSkillsPracticeModeEnabled !== false} onToggle={() => setLocalAdConfig({...localAdConfig, isSkillsPracticeModeEnabled: localAdConfig.isSkillsPracticeModeEnabled === false ? true : false})} />
                                <FeatureToggle label="Voice Sequencing Speech Practice" val={localAdConfig.isVoiceSequencingEnabled !== false} onToggle={() => setLocalAdConfig({...localAdConfig, isVoiceSequencingEnabled: localAdConfig.isVoiceSequencingEnabled === false ? true : false})} />
                                <FeatureToggle label="No Account Needed (Guest Trial Mode)" val={localAdConfig.isGuestTrialEnabled !== false} onToggle={() => setLocalAdConfig({...localAdConfig, isGuestTrialEnabled: localAdConfig.isGuestTrialEnabled === false ? true : false})} />
                            </div>

                            <div className="border-t border-dashed border-slate-100 dark:border-slate-800 pt-8 mt-6">
                                <h4 className="text-sm font-black uppercase text-indigo-600 dark:text-indigo-400 tracking-wider mb-4 flex items-center gap-1.5">
                                    <Sparkles size={14} className="animate-pulse" />
                                    Guest Trial & Exploration Configuration
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                                    <div className="flex flex-col gap-2 p-4 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border border-slate-105 dark:border-slate-800">
                                        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                            Guest Item Limit (Questions & Flashcards)
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input 
                                                type="number" 
                                                min="5" 
                                                max="500"
                                                value={localAdConfig.guestQuestionLimit !== undefined ? localAdConfig.guestQuestionLimit : 50} 
                                                onChange={e => setLocalAdConfig({...localAdConfig, guestQuestionLimit: parseInt(e.target.value) || 50})} 
                                                className="w-24 p-2 bg-white dark:bg-slate-800 border border-slate-205 dark:border-slate-700 rounded-xl font-extrabold text-sm text-center text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500" 
                                            />
                                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                                                items unauthenticated visitors can try before registration modal is triggered.
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-3.5 p-5 bg-indigo-50/40 dark:bg-slate-900/60 rounded-2xl border-2 border-indigo-100 dark:border-indigo-900/40 shadow-sm">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                            <div className="space-y-0.5">
                                                <div className="flex items-center gap-1.5">
                                                    <Sparkles size={13} className="text-indigo-600 dark:text-indigo-400" />
                                                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                        Guest Practice Exam Limit (Full Mock Tests)
                                                    </label>
                                                </div>
                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">
                                                    Default: 1 test (based on full state question count). Override anytime to increase or decrease.
                                                </p>
                                            </div>
                                            <span className={`text-[10px] font-black px-2.5 py-1 rounded-lg self-start sm:self-auto border shrink-0 transition-all ${
                                                (localAdConfig.guestExamLimit ?? 1) === 0
                                                    ? 'bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800/40'
                                                    : 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/40'
                                            }`}>
                                                {(localAdConfig.guestExamLimit ?? 1) === 0 
                                                    ? 'Locked • Sign-Up Required' 
                                                    : `${localAdConfig.guestExamLimit ?? 1} Free ${(localAdConfig.guestExamLimit ?? 1) === 1 ? 'Exam' : 'Exams'} Allowed`}
                                            </span>
                                        </div>

                                        {/* Stepper + Input */}
                                        <div className="flex flex-wrap items-center gap-3">
                                            <div className="flex items-center bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-1 shadow-sm">
                                                <button
                                                    type="button"
                                                    onClick={() => setLocalAdConfig({
                                                        ...localAdConfig,
                                                        guestExamLimit: Math.max(0, (localAdConfig.guestExamLimit ?? 1) - 1)
                                                    })}
                                                    disabled={(localAdConfig.guestExamLimit ?? 1) <= 0}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-black text-sm hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-40 transition-all active:scale-95"
                                                    title="Decrease limit by 1"
                                                >
                                                    -
                                                </button>
                                                <input 
                                                    type="number" 
                                                    min="0" 
                                                    max="100"
                                                    value={localAdConfig.guestExamLimit !== undefined ? localAdConfig.guestExamLimit : 1} 
                                                    onChange={e => setLocalAdConfig({
                                                        ...localAdConfig,
                                                        guestExamLimit: Math.max(0, parseInt(e.target.value) || 0)
                                                    })} 
                                                    className="w-16 p-1 bg-transparent font-black text-base text-center text-slate-900 dark:text-white focus:outline-none" 
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setLocalAdConfig({
                                                        ...localAdConfig,
                                                        guestExamLimit: (localAdConfig.guestExamLimit ?? 1) + 1
                                                    })}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-black text-sm hover:bg-slate-200 dark:hover:bg-slate-600 transition-all active:scale-95"
                                                    title="Increase limit by 1"
                                                >
                                                    +
                                                </button>
                                            </div>
                                            <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
                                                full state-standard practice {(localAdConfig.guestExamLimit ?? 1) === 1 ? 'test' : 'tests'} unauthenticated guests can complete before sign-up.
                                            </span>
                                        </div>

                                        {/* Quick Presets */}
                                        <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-indigo-100/60 dark:border-slate-800">
                                            <span className="text-[9px] font-black uppercase text-slate-400 mr-1">Quick Presets:</span>
                                            {[
                                                { val: 0, label: '0 (Lock All)' },
                                                { val: 1, label: '1 Test (Default)' },
                                                { val: 2, label: '2 Tests' },
                                                { val: 3, label: '3 Tests' },
                                                { val: 5, label: '5 Tests' },
                                                { val: 10, label: '10 Tests' },
                                            ].map(preset => {
                                                const isCurrent = (localAdConfig.guestExamLimit ?? 1) === preset.val;
                                                return (
                                                    <button
                                                        key={preset.val}
                                                        type="button"
                                                        onClick={() => setLocalAdConfig({ ...localAdConfig, guestExamLimit: preset.val })}
                                                        className={`px-3 py-1.5 text-[9px] font-black rounded-lg transition-all ${
                                                            isCurrent
                                                                ? 'bg-indigo-600 text-white shadow-sm ring-2 ring-indigo-400/40'
                                                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                                                        }`}
                                                    >
                                                        {preset.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-dashed border-slate-100 dark:border-slate-800 pt-8 mt-6">
                                <h4 className="text-sm font-black uppercase text-indigo-600 dark:text-indigo-400 tracking-wider mb-4 flex items-center gap-1.5">
                                    <Sparkles size={14} className="animate-pulse" />
                                    New Student Onboarding Experience
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                                    <FeatureToggle 
                                        label="Ultra-Simplified New Student Onboarding" 
                                        val={localAdConfig.onboardingFeatureEnabled !== false} 
                                        onToggle={() => setLocalAdConfig({...localAdConfig, onboardingFeatureEnabled: localAdConfig.onboardingFeatureEnabled === false ? true : false})} 
                                    />
                                    <div className="flex flex-col gap-2 p-4 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border border-slate-105 dark:border-slate-800">
                                        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                            Onboarding Active Duration (Hours)
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input 
                                                type="number" 
                                                min="1" 
                                                max="720"
                                                value={localAdConfig.onboardingHoursThreshold !== undefined ? localAdConfig.onboardingHoursThreshold : 48} 
                                                onChange={e => setLocalAdConfig({...localAdConfig, onboardingHoursThreshold: parseInt(e.target.value) || 48})} 
                                                className="w-24 p-2 bg-white dark:bg-slate-800 border border-slate-205 dark:border-slate-700 rounded-xl font-extrabold text-sm text-center text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500" 
                                            />
                                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                                                hours before newly signed-up users auto-transition to the active dashboard.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'mini_player' && (
                        <div className="bg-white rounded-[3rem] p-8 md:p-10 border border-slate-200 shadow-sm space-y-8 animate-slide-up">
                            {/* Header Banner */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-6">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-xl">
                                        <Headphones size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black tracking-tight">Mini Audio Player Content Manager</h3>
                                        <p className="text-slate-400 text-xs font-bold tracking-widest">
                                            Manage nursing concept audio summaries, upload media files, or configure embed links
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={handleResetTracks}
                                        className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all flex items-center gap-2"
                                        title="Restore standard nursing concept tracks"
                                    >
                                        <RotateCcw size={14} /> Reset Defaults
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleOpenAddTrack}
                                        className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs rounded-xl shadow-lg transition-all flex items-center gap-2"
                                    >
                                        <Plus size={16} /> Add Audio Summary
                                    </button>
                                </div>
                            </div>

                            {/* Global Feature Flag Control */}
                            <div className="p-6 bg-slate-950 text-white rounded-3xl flex flex-col md:flex-row items-center justify-between gap-4 border border-white/10 shadow-lg">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-indigo-500/20 text-indigo-400 rounded-xl">
                                        <Zap size={20} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-black text-white uppercase tracking-wider">Mini Player Feature Flag</h4>
                                        <p className="text-xs text-slate-400">
                                            {localAdConfig.isMiniPlayerEnabled !== false 
                                                ? 'Mini Audio Player is ENABLED across the app (floating widget & inline card visible).' 
                                                : 'Mini Audio Player is DISABLED globally. All player widgets and inline cards are hidden.'}
                                        </p>
                                    </div>
                                </div>
                                <FeatureToggle 
                                    label="Mini Player Active" 
                                    val={localAdConfig.isMiniPlayerEnabled !== false} 
                                    onToggle={() => setLocalAdConfig({...localAdConfig, isMiniPlayerEnabled: localAdConfig.isMiniPlayerEnabled === false ? true : false})} 
                                />
                            </div>

                            {/* Tracks List */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider">
                                        Active Audio Summaries ({getTracks().length} tracks)
                                    </h4>
                                    <span className="text-[10px] text-slate-400 font-bold">
                                        Use arrow controls to reorder sequence
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                    {getTracks().map((track, idx) => (
                                        <div 
                                            key={track.id || idx}
                                            className="p-5 bg-slate-50 hover:bg-slate-100/80 rounded-2xl border border-slate-200/80 transition-all flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4"
                                        >
                                            <div className="flex items-center gap-4 min-w-0 flex-1">
                                                <img 
                                                    src={track.img || 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=300&auto=format&fit=crop&q=80'} 
                                                    alt={track.title} 
                                                    className="w-14 h-14 rounded-xl object-cover border border-slate-200 shrink-0 shadow-xs"
                                                />
                                                <div className="min-w-0 flex-1 space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-[9px] font-black uppercase tracking-wider rounded-md">
                                                            {track.conceptCategory}
                                                        </span>
                                                        <span className="text-[10px] font-bold text-slate-500 tabular-nums">
                                                            ⏱️ {track.duration}
                                                        </span>
                                                    </div>
                                                    <h5 className="text-sm font-black text-slate-900 truncate">
                                                        {track.title}
                                                    </h5>
                                                    <p className="text-xs text-slate-500 line-clamp-1 font-medium">
                                                        "{track.script}"
                                                    </p>
                                                    {track.highYieldPoints && track.highYieldPoints.length > 0 && (
                                                        <p className="text-[10px] font-bold text-indigo-600">
                                                            💡 {track.highYieldPoints.length} key takeaway bullet points
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 shrink-0 self-end lg:self-center">
                                                <button
                                                    type="button"
                                                    disabled={idx === 0}
                                                    onClick={() => handleMoveTrack(idx, 'up')}
                                                    className="p-2 bg-white hover:bg-slate-200 text-slate-600 rounded-lg disabled:opacity-30 border border-slate-200 transition-colors"
                                                    title="Move Track Up"
                                                >
                                                    <ArrowUp size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={idx === getTracks().length - 1}
                                                    onClick={() => handleMoveTrack(idx, 'down')}
                                                    className="p-2 bg-white hover:bg-slate-200 text-slate-600 rounded-lg disabled:opacity-30 border border-slate-200 transition-colors"
                                                    title="Move Track Down"
                                                >
                                                    <ArrowDown size={14} />
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenEditTrack(track)}
                                                    className="px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-extrabold text-xs rounded-xl transition-colors flex items-center gap-1.5"
                                                >
                                                    <Edit3 size={14} /> Edit
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteTrack(track.id)}
                                                    className="p-2 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-xl transition-colors"
                                                    title="Delete Audio Summary"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'monetization' && (
                        <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm space-y-10 animate-slide-up">
                            <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
                                <div className="p-3 bg-amber-500 text-white rounded-2xl shadow-xl"><CreditCard size={24}/></div>
                                <div>
                                    <h3 className="text-xl font-black tracking-tight">Elite Pro Strategy</h3>
                                    <p className="text-slate-400 text-xs font-bold tracking-widest">Checkout URLs and Intermission Logic</p>
                                </div>
                            </div>
                            
                            <div className="space-y-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Elite Pro Checkout URL (Upgrade Now URL)</label>
                                    <input type="text" value={localAdConfig.eliteProUrl} onChange={e => setLocalAdConfig({...localAdConfig, eliteProUrl: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold outline-none focus:bg-white" placeholder="https://..." />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Elite Pro Display Price</label>
                                        <input type="text" value={localAdConfig.eliteProPrice || ''} onChange={e => setLocalAdConfig({...localAdConfig, eliteProPrice: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold outline-none focus:bg-white" placeholder="e.g. $49 or $19.99/mo" />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Showcase Video / Embed Presentation URL</label>
                                        <input type="text" value={localAdConfig.eliteProVideoUrl || ''} onChange={e => setLocalAdConfig({...localAdConfig, eliteProVideoUrl: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold outline-none focus:bg-white" placeholder="e.g. https://www.youtube.com/embed/..." />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-4">
                                        <FeatureToggle label="Show Upgrade Hints" val={localAdConfig.showUpgradeHint} onToggle={() => setLocalAdConfig({...localAdConfig, showUpgradeHint: !localAdConfig.showUpgradeHint})} />
                                        <FeatureToggle label="Overlay Intermission" val={localAdConfig.overlaySettings.enabled} onToggle={() => setLocalAdConfig({...localAdConfig, overlaySettings: {...localAdConfig.overlaySettings, enabled: !localAdConfig.overlaySettings.enabled }})} />
                                        
                                        <div className="space-y-2 pt-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Intermission Ad Source</label>
                                            <div className="flex bg-slate-50 p-1 rounded-2xl border border-slate-200">
                                                {['internal', 'network'].map(s => (
                                                    <button 
                                                        key={s} 
                                                        onClick={() => setPlacementSource('overlay', s as any)} 
                                                        className={`flex-1 py-3 rounded-xl text-[10px] font-black transition-all ${localAdConfig.placements.overlay?.source === s ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}
                                                    >
                                                        {s}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest">Overlay Trigger (Min)</label>
                                            <span className="font-black text-indigo-600">{localAdConfig.overlaySettings.triggerMinutes}m</span>
                                        </div>
                                        <input type="range" min="1" max="60" value={localAdConfig.overlaySettings.triggerMinutes} onChange={e => setLocalAdConfig({...localAdConfig, overlaySettings: {...localAdConfig.overlaySettings, triggerMinutes: parseInt(e.target.value)}})} className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
                                    </div>
                                </div>

                                <div className="pt-8 border-t border-slate-100">
                                    <h4 className="text-[10px] font-black text-slate-400 tracking-[0.3em] mb-6">Simulation Intermission Intervals (Items)</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        <IntervalInput label="Study Guide" val={localAdConfig.intervals.studyGuide} onChange={v => setLocalAdConfig({...localAdConfig, intervals: {...localAdConfig.intervals, studyGuide: v}})} />
                                        <IntervalInput label="Skill Lab" val={localAdConfig.intervals.skills_lab} onChange={v => setLocalAdConfig({...localAdConfig, intervals: {...localAdConfig.intervals, skills_lab: v}})} />
                                        <IntervalInput label="Practice Quiz" val={localAdConfig.intervals.practiceQuiz} onChange={v => setLocalAdConfig({...localAdConfig, intervals: {...localAdConfig.intervals, practiceQuiz: v}})} />
                                        <IntervalInput label="Flashcards" val={localAdConfig.intervals.flashcards} onChange={v => setLocalAdConfig({...localAdConfig, intervals: {...localAdConfig.intervals, flashcards: v}})} />
                                        <IntervalInput label="Board Exam" val={localAdConfig.intervals.practiceExam} onChange={v => setLocalAdConfig({...localAdConfig, intervals: {...localAdConfig.intervals, practiceExam: v}})} />
                                        <IntervalInput label="Skip Timer (Sec)" val={localAdConfig.intervals.intermissionSkipSeconds} onChange={v => setLocalAdConfig({...localAdConfig, intervals: {...localAdConfig.intervals, intermissionSkipSeconds: v}})} />
                                        <IntervalInput label="Ad Rotation Frequency" val={localAdConfig.intervals.adRotationThreshold} onChange={v => setLocalAdConfig({...localAdConfig, intervals: {...localAdConfig.intervals, adRotationThreshold: v}})} />
                                    </div>
                                    <p className="mt-4 text-[9px] font-bold text-slate-400">Rotation Frequency: Refresh ads automatically every X questions/actions/cards in sessions (Quiz, Exam, and Flashcards).</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'governance' && (
                        <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm space-y-10 animate-slide-up">
                            <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
                                <div className="p-3 bg-rose-600 text-white rounded-2xl shadow-xl"><ShieldAlert size={24}/></div>
                                <div>
                                    <h3 className="text-xl font-black tracking-tight">Platform Governance</h3>
                                    <p className="text-slate-400 text-xs font-bold tracking-widest">Maintenance Mode & Global Layout Policy</p>
                                </div>
                            </div>

                            <div className="space-y-8">
                                <div className="p-8 bg-rose-50 border-2 border-rose-100 rounded-[2.5rem] space-y-6">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h4 className="text-sm font-black text-rose-900">Emergency Lockout</h4>
                                            <p className="text-[10px] font-bold text-rose-400 mt-1">Restrict all student access immediately</p>
                                        </div>
                                        <ToggleIcon enabled={localAdConfig.isMaintenanceMode} onClick={() => setLocalAdConfig({...localAdConfig, isMaintenanceMode: !localAdConfig.isMaintenanceMode})} />
                                    </div>
                                    <div className={`space-y-4 transition-opacity ${localAdConfig.isMaintenanceMode ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                                        <input type="text" value={localAdConfig.maintenanceTitle} onChange={e => setLocalAdConfig({...localAdConfig, maintenanceTitle: e.target.value})} className="w-full p-4 bg-white border border-rose-200 rounded-xl font-black text-xs" placeholder="Lockout Title..." />
                                        <textarea value={localAdConfig.maintenanceMessage} onChange={e => setLocalAdConfig({...localAdConfig, maintenanceMessage: e.target.value})} className="w-full p-4 bg-white border border-rose-200 rounded-xl font-medium text-xs h-24" placeholder="Instructional message..." />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Header Display Mode</label>
                                        <div className="flex bg-slate-50 p-1 rounded-2xl border border-slate-200">
                                            {['fixed', 'scroll'].map(m => (
                                                <button key={m} onClick={() => setLocalAdConfig({...localAdConfig, headerMode: m as any})} className={`flex-1 py-3 rounded-xl text-[10px] font-black transition-all ${localAdConfig.headerMode === m ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>{m}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Footer Layout Style</label>
                                        <div className="flex bg-slate-50 p-1 rounded-2xl border border-slate-200">
                                            {['standard', 'mini'].map(f => (
                                                <button key={f} onClick={() => setLocalAdConfig({...localAdConfig, footerType: f as any})} className={`flex-1 py-3 rounded-xl text-[10px] font-black transition-all ${localAdConfig.footerType === f ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>{f}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <FeatureToggle label="Slim Mobile Header" val={localAdConfig.headerSlimMobile} onToggle={() => setLocalAdConfig({...localAdConfig, headerSlimMobile: !localAdConfig.headerSlimMobile})} />
                                    <FeatureToggle label="Global Footer Visibility" val={localAdConfig.isFooterEnabled} onToggle={() => setLocalAdConfig({...localAdConfig, isFooterEnabled: !localAdConfig.isFooterEnabled})} />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'compliance' && (
                        <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm space-y-10 animate-slide-up">
                            <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
                                <div className="p-3 bg-emerald-600 text-white rounded-2xl shadow-xl"><ShieldCheck size={24}/></div>
                                <div>
                                    <h3 className="text-xl font-black tracking-tight">Compliance & Privacy</h3>
                                    <p className="text-slate-400 text-xs font-bold tracking-widest">Manage Cookie Consent Banner & Data Policies</p>
                                </div>
                            </div>

                            <div className="space-y-8">
                                <div className="p-8 bg-emerald-50 border-2 border-emerald-100 rounded-[2.5rem] space-y-6">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h4 className="text-sm font-black text-emerald-900">Cookie Consent Banner</h4>
                                            <p className="text-[10px] font-bold text-emerald-400 mt-1">Enable GDPR/CCPA compliant banner for users</p>
                                        </div>
                                        <ToggleIcon 
                                            enabled={localAdConfig.cookieConsent.isEnabled} 
                                            onClick={() => setLocalAdConfig({
                                                ...localAdConfig, 
                                                cookieConsent: { ...localAdConfig.cookieConsent, isEnabled: !localAdConfig.cookieConsent.isEnabled }
                                            })} 
                                        />
                                    </div>
                                    
                                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity ${localAdConfig.cookieConsent.isEnabled ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Banner Title</label>
                                            <input 
                                                type="text" 
                                                value={localAdConfig.cookieConsent.title} 
                                                onChange={e => setLocalAdConfig({
                                                    ...localAdConfig, 
                                                    cookieConsent: { ...localAdConfig.cookieConsent, title: e.target.value }
                                                })} 
                                                className="w-full p-4 bg-white border border-emerald-200 rounded-xl font-black text-xs" 
                                                placeholder="Consent Title..." 
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Privacy Policy URL</label>
                                            <input 
                                                type="text" 
                                                value={localAdConfig.cookieConsent.policyUrl} 
                                                onChange={e => setLocalAdConfig({
                                                    ...localAdConfig, 
                                                    cookieConsent: { ...localAdConfig.cookieConsent, policyUrl: e.target.value }
                                                })} 
                                                className="w-full p-4 bg-white border border-emerald-200 rounded-xl font-medium text-xs" 
                                                placeholder="/privacy" 
                                            />
                                        </div>
                                        <div className="md:col-span-2 space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Consent Message</label>
                                            <textarea 
                                                value={localAdConfig.cookieConsent.message} 
                                                onChange={e => setLocalAdConfig({
                                                    ...localAdConfig, 
                                                    cookieConsent: { ...localAdConfig.cookieConsent, message: e.target.value }
                                                })} 
                                                className="w-full p-4 bg-white border border-emerald-200 rounded-xl font-medium text-xs h-24 resize-none" 
                                                placeholder="We use cookies to enhance your experience..." 
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Accept Button Text</label>
                                            <input 
                                                type="text" 
                                                value={localAdConfig.cookieConsent.acceptButtonText} 
                                                onChange={e => setLocalAdConfig({
                                                    ...localAdConfig, 
                                                    cookieConsent: { ...localAdConfig.cookieConsent, acceptButtonText: e.target.value }
                                                })} 
                                                className="w-full p-4 bg-white border border-emerald-200 rounded-xl font-black text-[10px] tracking-widest" 
                                                placeholder="ACCEPT ALL" 
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Decline Button Text</label>
                                            <input 
                                                type="text" 
                                                value={localAdConfig.cookieConsent.declineButtonText} 
                                                onChange={e => setLocalAdConfig({
                                                    ...localAdConfig, 
                                                    cookieConsent: { ...localAdConfig.cookieConsent, declineButtonText: e.target.value }
                                                })} 
                                                className="w-full p-4 bg-white border border-emerald-200 rounded-xl font-black text-[10px] tracking-widest" 
                                                placeholder="DECLINE" 
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 bg-indigo-50 rounded-[2rem] border border-indigo-100 flex items-start gap-4">
                                    <Shield size={24} className="text-indigo-600 shrink-0 mt-1" />
                                    <div className="space-y-2">
                                        <h4 className="text-[11px] font-black text-indigo-900 tracking-tight">Compliance Protocol GA-8</h4>
                                        <p className="text-[10px] font-medium text-indigo-700 leading-relaxed opacity-80">
                                            "Enabling the consent banner will automatically defer non-essential tracking scripts until explicit user authorization is received. This ensures compliance with global data protection standards."
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'themes' && (
                        <div className="bg-white p-8 md:p-10 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-8 animate-slide-up">
                            {/* Header */}
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-100 pb-6">
                                <div className="space-y-1">
                                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black tracking-widest border border-indigo-100">
                                        <Sparkles size={12} /> System Theme Engine
                                    </div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">Theme Option Architecture</h3>
                                    <p className="text-xs text-slate-500 font-medium">Select and deploy platform UI themes across candidate dashboards and exam pathways.</p>
                                </div>

                                <div className="px-4 py-2 bg-slate-900 text-white rounded-2xl flex items-center gap-3 shadow-md">
                                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></div>
                                    <span className="text-[10px] font-black tracking-widest uppercase">
                                        Active: {localAdConfig.activeTheme === 'clinical_trust' ? 'Clinical / Medical Trust' : localAdConfig.activeTheme === 'warm_academic' ? 'Warm Academic Theme' : (localAdConfig.activeTheme === 'structured_course' || localAdConfig.activeTheme === 'structured_course_platform') ? 'Structured Course-Platform Theme' : 'Default Platform Theme'}
                                    </span>
                                </div>
                            </div>

                            {/* Centralized Header Branding & School Logo Configuration */}
                            <div className="p-6 bg-slate-50 dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">Header School Logo Branding</h4>
                                        <p className="text-xs text-slate-500">Centralized transparent logo displayed on the left side of the header across all themes.</p>
                                    </div>
                                    {(localAdConfig.schoolLogoUrl || 'https://static.wixstatic.com/shapes/cc4206_330c2df5c9be45088f9d7ba5b00d2a6d.svg') && (
                                        <div className="p-2 bg-transparent flex items-center justify-center">
                                            <img src={localAdConfig.schoolLogoUrl || 'https://static.wixstatic.com/shapes/cc4206_330c2df5c9be45088f9d7ba5b00d2a6d.svg'} alt="Header School Logo" className="h-9 w-auto object-contain max-w-[200px]" />
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase">School Logo SVG / Image URL</label>
                                    <div className="flex gap-3">
                                        <input 
                                            type="text" 
                                            value={localAdConfig.schoolLogoUrl || ''} 
                                            onChange={e => setLocalAdConfig({ ...localAdConfig, schoolLogoUrl: e.target.value })} 
                                            className="flex-1 p-3.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-mono" 
                                            placeholder="https://static.wixstatic.com/shapes/cc4206_330c2df5c9be45088f9d7ba5b00d2a6d.svg" 
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => setLocalAdConfig({ ...localAdConfig, schoolLogoUrl: 'https://static.wixstatic.com/shapes/cc4206_330c2df5c9be45088f9d7ba5b00d2a6d.svg' })}
                                            className="px-4 py-3 bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl font-bold text-xs hover:bg-slate-300 transition-all"
                                        >
                                            Reset Logo
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* 5 Theme Options Grid */}
                            <div className="space-y-6">
                                
                                {/* Theme 1: Default Theme */}
                                <div className={`p-6 rounded-[2rem] border-2 transition-all relative overflow-hidden ${(!localAdConfig.activeTheme || localAdConfig.activeTheme === 'default') ? 'border-indigo-600 bg-indigo-50/10 shadow-xl' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                        <div className="space-y-3 flex-1">
                                            <div className="flex items-center gap-3">
                                                <span className="px-3 py-1 bg-slate-900 text-white rounded-lg text-[9px] font-black tracking-widest">DEFAULT THEME</span>
                                                {(!localAdConfig.activeTheme || localAdConfig.activeTheme === 'default') && (
                                                    <span className="text-[10px] font-black text-emerald-600 flex items-center gap-1"><CheckCircle size={14} /> ACTIVE PLATFORM DEFAULT</span>
                                                )}
                                            </div>
                                            <h4 className="text-lg font-black text-slate-900">Classic Study Platform</h4>
                                            <p className="text-xs text-slate-500 leading-relaxed font-medium">
                                                Standard blue and neutral study interface with full gamification metrics, vibrant cards, and classic layout hierarchy.
                                            </p>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md text-[9px] font-bold">Classic Indigo (#4F46E5)</span>
                                                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md text-[9px] font-bold">Full Gamification</span>
                                                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md text-[9px] font-bold">Sans-serif</span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleDeployTheme('default')}
                                            disabled={!localAdConfig.activeTheme || localAdConfig.activeTheme === 'default'}
                                            className={`px-6 py-3.5 rounded-2xl font-black text-[10px] tracking-widest transition-all shrink-0 ${(!localAdConfig.activeTheme || localAdConfig.activeTheme === 'default') ? 'bg-slate-100 text-slate-400 cursor-default' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md active:scale-95'}`}
                                        >
                                            {(!localAdConfig.activeTheme || localAdConfig.activeTheme === 'default') ? 'CURRENT ACTIVE' : 'DEPLOY DEFAULT'}
                                        </button>
                                    </div>
                                </div>

                                {/* Theme 2: Option 1 - Clinical / Medical Trust Theme */}
                                <div className={`p-6 rounded-[2rem] border-2 transition-all relative overflow-hidden ${localAdConfig.activeTheme === 'clinical_trust' ? 'border-[#0F7D6C] bg-emerald-50/20 shadow-xl' : 'border-slate-200 bg-white hover:border-[#0F7D6C]/50'}`}>
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                        <div className="space-y-3 flex-1">
                                            <div className="flex items-center gap-3">
                                                <span className="px-3 py-1 bg-[#0B2942] text-white rounded-lg text-[9px] font-black tracking-widest">OPTION 1 THEME</span>
                                                {localAdConfig.activeTheme === 'clinical_trust' && (
                                                    <span className="text-[10px] font-black text-[#0F7D6C] flex items-center gap-1"><CheckCircle size={14} /> ACTIVE PLATFORM THEME</span>
                                                )}
                                                <span className="px-2.5 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[9px] font-black">UWorld & ATI Nursing Feel</span>
                                            </div>
                                            <h4 className="text-lg font-serif font-bold text-[#0B2942]">Clinical / Medical Trust Theme</h4>
                                            <p className="text-xs text-slate-600 leading-relaxed font-normal">
                                                Leads with authority and credibility over gamification. Features deep navy hero headers (#0B2942), teal accent elements (#0F7D6C), serif textbook titles, 50-state certification trust strip, and gold credential seals (DNP, NP, RN, MSN).
                                            </p>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                <span className="px-2.5 py-1 bg-[#0B2942] text-white rounded-md text-[9px] font-bold">Deep Navy #0B2942</span>
                                                <span className="px-2.5 py-1 bg-teal-100 text-teal-800 rounded-md text-[9px] font-bold">Teal Accent #0F7D6C</span>
                                                <span className="px-2.5 py-1 bg-amber-100 text-amber-800 rounded-md text-[9px] font-bold">Credential Seals (DNP/RN)</span>
                                                <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-[9px] font-bold">Serif Typography</span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleDeployTheme('clinical_trust')}
                                            disabled={localAdConfig.activeTheme === 'clinical_trust'}
                                            className={`px-6 py-3.5 rounded-2xl font-black text-[10px] tracking-widest transition-all shrink-0 ${localAdConfig.activeTheme === 'clinical_trust' ? 'bg-slate-100 text-slate-400 cursor-default' : 'bg-[#0F7D6C] text-white hover:bg-[#0B6356] shadow-md active:scale-95'}`}
                                        >
                                            {localAdConfig.activeTheme === 'clinical_trust' ? 'CURRENT ACTIVE' : 'DEPLOY CLINICAL TRUST'}
                                        </button>
                                    </div>
                                </div>

                                {/* Theme 3: Option 2 - Warm Academic Theme */}
                                <div className={`p-6 rounded-[2rem] border-2 transition-all relative overflow-hidden ${localAdConfig.activeTheme === 'warm_academic' ? 'border-[#0D6E6E] bg-[#E6F4F1]/40 shadow-xl' : 'border-slate-200 bg-white hover:border-[#0D6E6E]/50'}`}>
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                        <div className="space-y-3 flex-1">
                                            <div className="flex items-center gap-3">
                                                <span className="px-3 py-1 bg-[#0D6E6E] text-white rounded-lg text-[9px] font-black tracking-widest">OPTION 2 THEME</span>
                                                {localAdConfig.activeTheme === 'warm_academic' && (
                                                    <span className="text-[10px] font-black text-[#0D6E6E] flex items-center gap-1"><CheckCircle size={14} /> ACTIVE PLATFORM THEME</span>
                                                )}
                                                <span className="px-2.5 py-0.5 bg-orange-100 text-orange-800 rounded-full text-[9px] font-black">Khan Academy & Coursera Feel</span>
                                            </div>
                                            <h4 className="text-lg font-bold text-[#2D3748]">Warm Academic Theme</h4>
                                            <p className="text-xs text-[#4A5568] leading-relaxed font-medium">
                                                Welcoming, approachable, and education-focused layout designed for healthcare adult learners and career changers. Features soft blue-green teal (#0D6E6E), warm off-white canvas (#F8F7F3), soft rounded cards, and warm coral/orange primary CTAs (#E05638).
                                            </p>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                <span className="px-2.5 py-1 bg-[#0D6E6E] text-white rounded-md text-[9px] font-bold">Soft Teal #0D6E6E</span>
                                                <span className="px-2.5 py-1 bg-[#F8F7F3] text-[#2D3748] border border-[#E5E3DC] rounded-md text-[9px] font-bold">Warm Off-White #F8F7F3</span>
                                                <span className="px-2.5 py-1 bg-[#E05638] text-white rounded-md text-[9px] font-bold">Warm Coral CTA #E05638</span>
                                                <span className="px-2.5 py-1 bg-[#E6F4F1] text-[#0D6E6E] rounded-md text-[9px] font-bold">Soft Rounded Cards</span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleDeployTheme('warm_academic')}
                                            disabled={localAdConfig.activeTheme === 'warm_academic'}
                                            className={`px-6 py-3.5 rounded-2xl font-black text-[10px] tracking-widest transition-all shrink-0 ${localAdConfig.activeTheme === 'warm_academic' ? 'bg-slate-100 text-slate-400 cursor-default' : 'bg-[#E05638] text-white hover:bg-[#C8472B] shadow-md active:scale-95'}`}
                                        >
                                            {localAdConfig.activeTheme === 'warm_academic' ? 'CURRENT ACTIVE' : 'DEPLOY WARM ACADEMIC'}
                                        </button>
                                    </div>
                                </div>

                                {/* Theme 4: Option 3 - Structured Course-Platform Theme */}
                                <div className={`p-6 rounded-[2rem] border-2 transition-all relative overflow-hidden ${localAdConfig.activeTheme === 'structured_course' || localAdConfig.activeTheme === 'structured_course_platform' ? 'border-[#1D4ED8] bg-blue-50/30 shadow-xl' : 'border-slate-200 bg-white hover:border-[#1D4ED8]/50'}`}>
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                        <div className="space-y-3 flex-1">
                                            <div className="flex items-center gap-3">
                                                <span className="px-3 py-1 bg-[#1D4ED8] text-white rounded-lg text-[9px] font-black tracking-widest">OPTION 3 THEME</span>
                                                {(localAdConfig.activeTheme === 'structured_course' || localAdConfig.activeTheme === 'structured_course_platform') && (
                                                    <span className="text-[10px] font-black text-[#1D4ED8] flex items-center gap-1"><CheckCircle size={14} /> ACTIVE PLATFORM THEME</span>
                                                )}
                                                <span className="px-2.5 py-0.5 bg-blue-100 text-blue-800 rounded-full text-[9px] font-black">Coursera & Udemy Course Catalog Feel</span>
                                            </div>
                                            <h4 className="text-lg font-black text-slate-900">Structured Course-Platform Theme</h4>
                                            <p className="text-xs text-slate-600 leading-relaxed font-medium">
                                                Syllabus-like learning environment inspired by Coursera & Udemy. Features one strong brand color (#1D4ED8 Deep Royal Blue), sequential module numbering (Modules 1–8), white cards, clean breadcrumbs, and a distraction-free active exam view.
                                            </p>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                <span className="px-2.5 py-1 bg-[#1D4ED8] text-white rounded-md text-[9px] font-bold">Single Brand Blue #1D4ED8</span>
                                                <span className="px-2.5 py-1 bg-blue-50 text-[#1D4ED8] border border-blue-200 rounded-md text-[9px] font-bold">Module Numbering (1–8)</span>
                                                <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-[9px] font-bold">Course Curriculum Layout</span>
                                                <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-[9px] font-bold">Distraction-Free Exam</span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleDeployTheme('structured_course')}
                                            disabled={localAdConfig.activeTheme === 'structured_course' || localAdConfig.activeTheme === 'structured_course_platform'}
                                            className={`px-6 py-3.5 rounded-2xl font-black text-[10px] tracking-widest transition-all shrink-0 ${(localAdConfig.activeTheme === 'structured_course' || localAdConfig.activeTheme === 'structured_course_platform') ? 'bg-slate-100 text-slate-400 cursor-default' : 'bg-[#1D4ED8] text-white hover:bg-[#1E40AF] shadow-md active:scale-95'}`}
                                        >
                                            {(localAdConfig.activeTheme === 'structured_course' || localAdConfig.activeTheme === 'structured_course_platform') ? 'CURRENT ACTIVE' : 'DEPLOY STRUCTURED COURSE'}
                                        </button>
                                    </div>
                                </div>

                                {/* Theme 5: Option 4 - Vibrant Academic Theme (Planned) */}
                                <div className="p-6 rounded-[2rem] border-2 border-slate-200 bg-slate-50/50 opacity-80">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                        <div className="space-y-3 flex-1">
                                            <div className="flex items-center gap-3">
                                                <span className="px-3 py-1 bg-slate-200 text-slate-700 rounded-lg text-[9px] font-black tracking-widest">OPTION 4 THEME</span>
                                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">PLANNED VARIANT</span>
                                            </div>
                                            <h4 className="text-lg font-black text-slate-800">Vibrant Learning Hub</h4>
                                            <p className="text-xs text-slate-500 leading-relaxed font-medium">
                                                Color-coded clinical domain accents, interactive progress rings, and engaging visual reward indicators.
                                            </p>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                <span className="px-2.5 py-1 bg-slate-200 text-slate-700 rounded-md text-[9px] font-bold">Domain Colors</span>
                                                <span className="px-2.5 py-1 bg-slate-200 text-slate-700 rounded-md text-[9px] font-bold">Visual Mastery</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setLocalAdConfig({ ...localAdConfig, activeTheme: 'vibrant_academic' });
                                            }}
                                            className="px-6 py-3.5 rounded-2xl font-black text-[10px] tracking-widest bg-slate-200 text-slate-700 hover:bg-slate-300 transition-all shrink-0"
                                        >
                                            ACTIVATE VIBRANT
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT COLUMN: Diagnostic Sandbox */}
                <div className="xl:col-span-5 space-y-6">
                    <div className="bg-white rounded-[3rem] border-2 border-indigo-500 shadow-[0_30px_80px_rgba(79,70,229,0.15)] overflow-hidden flex flex-col h-[700px] animate-pop relative">
                        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-600 text-white rounded-xl shadow-lg animate-pulse"><Zap size={18}/></div>
                                <div>
                                    <h3 className="text-sm font-black tracking-tight">Live Diagnostic Sandbox</h3>
                                    <p className="text-[8px] font-black text-slate-400 tracking-widest">Real-Time Registry Simulation</p>
                                </div>
                            </div>
                            <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                                <button onClick={() => setPreviewMode('desktop')} className={`p-2 rounded-lg transition-all ${previewMode === 'desktop' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400'}`}><MonitorDot size={18}/></button>
                                <button onClick={() => setPreviewMode('mobile')} className={`p-2 rounded-lg transition-all ${previewMode === 'mobile' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400'}`}><Smartphone size={18}/></button>
                            </div>
                        </div>

                        <div className="p-6 bg-slate-100 flex items-center justify-center gap-4 overflow-x-auto no-scrollbar border-b border-slate-200">
                             <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-slate-400">Focusing Unit:</span>
                                <div className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-[9px] font-black text-indigo-600 shadow-sm">
                                    {activePlacement.replace(/_/g, ' ')}
                                </div>
                             </div>
                             <div className="w-px h-4 bg-slate-300"></div>
                             <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-slate-400">Source:</span>
                                <div className={`px-3 py-1 border rounded-lg text-[9px] font-black shadow-sm ${localAdConfig.placements[activePlacement]?.source === 'network' ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                                    {localAdConfig.placements[activePlacement]?.source}
                                </div>
                             </div>
                        </div>

                        <div className="flex-1 bg-slate-200/50 p-6 md:p-10 overflow-y-auto custom-scrollbar flex items-start justify-center">
                            <div 
                                className={`bg-white shadow-2xl transition-all duration-700 overflow-hidden relative border-4 border-slate-300 rounded-[2rem] md:rounded-[3rem] ${previewMode === 'mobile' ? 'w-[320px] min-h-[500px]' : 'w-full min-h-[400px]'}`}
                            >
                                <div className="h-10 bg-slate-50 border-b border-slate-100 px-4 flex items-center justify-between">
                                    <div className="flex gap-1.5">
                                        <div className="w-2 h-2 rounded-full bg-slate-200"></div>
                                        <div className="w-2 h-2 rounded-full bg-slate-200"></div>
                                        <div className="w-2 h-2 rounded-full bg-slate-200"></div>
                                    </div>
                                    <span className="text-[8px] font-black text-slate-300 tracking-widest">Clinical Dashboard Simulation</span>
                                </div>

                                <div className="p-2 md:p-6 space-y-6">
                                    <div className="space-y-2">
                                        <div className="h-4 w-2/3 bg-slate-100 rounded-md"></div>
                                        <div className="h-2 w-full bg-slate-50 rounded-md"></div>
                                        <div className="h-2 w-5/6 bg-slate-50 rounded-md"></div>
                                    </div>

                                    <div className="relative group/sandbox">
                                        <div className="absolute -top-3 -left-3 z-20 px-2 py-1 bg-indigo-600 text-white rounded text-[7px] font-black shadow-lg opacity-0 group-hover/sandbox:opacity-100 transition-opacity">
                                            Target Node: {activePlacement}
                                        </div>
                                        <AdUnit key={previewKey} placement={activePlacement} />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="h-24 bg-slate-50 rounded-2xl"></div>
                                        <div className="h-24 bg-slate-50 rounded-2xl"></div>
                                    </div>
                                </div>
                                
                                <div className="absolute inset-0 border-4 border-dashed border-indigo-400/20 pointer-events-none rounded-[2rem] md:rounded-[3rem]"></div>
                            </div>
                        </div>

                        <div className="p-6 bg-slate-950 text-white flex items-center justify-between border-t border-white/5 shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-white/5 rounded-xl border border-white/10 text-indigo-400">
                                    <Activity size={18}/>
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-indigo-300">Logic Integrity Audit</p>
                                    <p className="text-[8px] font-bold text-slate-500 tracking-widest mt-0.5">Verified script propagation active</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => {
                                    const next = {...localAdConfig};
                                    setLocalAdConfig(next);
                                    setPreviewKey(Date.now());
                                }} 
                                className="px-6 py-2 bg-white/5 border border-white/10 hover:bg-white/10 rounded-xl font-black text-[9px] tracking-widest transition-all"
                            >
                                Clear Session Cache
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 border-b border-slate-50 pb-4">
                            <ShieldCheck size={20} className="text-emerald-500" />
                            <h4 className="font-black tracking-tight text-slate-800">Verification Logic</h4>
                        </div>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0"></div>
                                <p className="text-[10px] font-bold text-slate-500 leading-relaxed">Network sources require a valid Publisher ID in Tracking & SDKs.</p>
                            </li>
                            <li className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0"></div>
                                <p className="text-[10px] font-bold text-slate-500 leading-relaxed">AdSense Filling behavior depends on Google's crawler access to this domain.</p>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Modal for Adding / Editing Audio Summary Tracks */}
            {isTrackModalOpen && (
                <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white text-slate-900 w-full max-w-2xl rounded-[2.5rem] p-6 md:p-8 shadow-2xl border border-slate-200 space-y-6 my-8 animate-scale-up">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-indigo-600 text-white rounded-xl">
                                    <Mic2 size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black tracking-tight">
                                        {trackEditing ? 'Edit Concept Audio Summary' : 'Add New Concept Audio Summary'}
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold">
                                        Configure title, category, media URLs, uploads, script & key points
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsTrackModalOpen(false)}
                                className="p-2 text-slate-400 hover:text-slate-600 rounded-xl transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-2 scrollbar-thin">
                            {/* Title */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                    Concept Summary Title *
                                </label>
                                <input
                                    type="text"
                                    value={trackForm.title}
                                    onChange={(e) => setTrackForm({ ...trackForm, title: e.target.value })}
                                    placeholder="e.g. Vital Signs: Normal Ranges & Measurement Rules"
                                    className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-900 outline-none focus:border-indigo-500"
                                />
                            </div>

                            {/* Category & Duration */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                        Concept Category
                                    </label>
                                    <select
                                        value={trackForm.conceptCategory}
                                        onChange={(e) => setTrackForm({ ...trackForm, conceptCategory: e.target.value })}
                                        className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-900 outline-none focus:border-indigo-500"
                                    >
                                        <option value="Clinical & Vitals">Clinical & Vitals</option>
                                        <option value="Safety & Prevention">Safety & Prevention</option>
                                        <option value="ADLs & Mobility">ADLs & Mobility</option>
                                        <option value="Personal ADL Care">Personal ADL Care</option>
                                        <option value="Principles & Rights">Principles & Rights</option>
                                        <option value="Board Prep & Basics">Board Prep & Basics</option>
                                        <option value="Custom Module">Custom Module</option>
                                    </select>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                        Duration (mm:ss)
                                    </label>
                                    <input
                                        type="text"
                                        value={trackForm.duration}
                                        onChange={(e) => setTrackForm({ ...trackForm, duration: e.target.value })}
                                        placeholder="3:45"
                                        className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-900 outline-none focus:border-indigo-500"
                                    />
                                </div>
                            </div>

                            {/* Thumbnail Cover Image */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                    Thumbnail Cover Image (URL or File Upload)
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={trackForm.img}
                                        onChange={(e) => setTrackForm({ ...trackForm, img: e.target.value })}
                                        placeholder="https://images.unsplash.com/..."
                                        className="flex-1 p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-900 outline-none focus:border-indigo-500"
                                    />
                                    <label className="px-4 py-3.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-2xl cursor-pointer transition-colors shrink-0 flex items-center gap-1.5">
                                        <Upload size={14} /> Upload Image
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleTrackFileUpload(e, 'img')}
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                                {trackForm.img && (
                                    <div className="flex items-center gap-3 pt-1">
                                        <img src={trackForm.img} alt="Preview" className="w-10 h-10 rounded-lg object-cover border" />
                                        <span className="text-[10px] text-emerald-600 font-extrabold flex items-center gap-1">
                                            <CheckCircle size={12} /> Thumbnail Ready
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Audio Source / Upload */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                    Audio File (URL or Local Audio Upload) *
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={trackForm.source}
                                        onChange={(e) => setTrackForm({ ...trackForm, source: e.target.value })}
                                        placeholder="https://cdn.example.com/audio.mp3"
                                        className="flex-1 p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-900 outline-none focus:border-indigo-500"
                                    />
                                    <label className="px-4 py-3.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-2xl cursor-pointer transition-colors shrink-0 flex items-center gap-1.5">
                                        <Upload size={14} /> Upload Audio File
                                        <input
                                            type="file"
                                            accept="audio/*"
                                            onChange={(e) => handleTrackFileUpload(e, 'source')}
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                                {uploadingField === 'source' && (
                                    <p className="text-xs text-indigo-600 font-bold animate-pulse">Reading audio file...</p>
                                )}
                                {trackForm.source && (
                                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl space-y-1">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase">Audio Preview Player:</p>
                                        <audio controls src={trackForm.source} className="w-full h-8" />
                                    </div>
                                )}
                            </div>

                            {/* Audio Script Summary */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                    Audio Script Summary / Concept Overview
                                </label>
                                <textarea
                                    rows={3}
                                    value={trackForm.script}
                                    onChange={(e) => setTrackForm({ ...trackForm, script: e.target.value })}
                                    placeholder="Vital signs monitor baseline physiological status..."
                                    className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-medium text-slate-900 outline-none focus:border-indigo-500 leading-relaxed"
                                />
                            </div>

                            {/* High Yield Key Takeaways */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                        High-Yield Key Takeaways (Bullet Points)
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setTrackForm(prev => ({ ...prev, highYieldPoints: [...prev.highYieldPoints, ''] }))}
                                        className="text-[10px] font-black uppercase text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                                    >
                                        <Plus size={12} /> Add Point
                                    </button>
                                </div>
                                {trackForm.highYieldPoints.map((pt, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={pt}
                                            onChange={(e) => {
                                                const updated = [...trackForm.highYieldPoints];
                                                updated[index] = e.target.value;
                                                setTrackForm({ ...trackForm, highYieldPoints: updated });
                                            }}
                                            placeholder={`Key Takeaway #${index + 1}`}
                                            className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-indigo-500"
                                        />
                                        {trackForm.highYieldPoints.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updated = trackForm.highYieldPoints.filter((_, i) => i !== index);
                                                    setTrackForm({ ...trackForm, highYieldPoints: updated });
                                                }}
                                                className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Modal Actions */}
                        <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
                            <button
                                type="button"
                                onClick={() => setIsTrackModalOpen(false)}
                                className="px-5 py-2.5 bg-slate-100 text-slate-700 font-bold text-xs rounded-xl hover:bg-slate-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveTrack}
                                disabled={!trackForm.title.trim() || !trackForm.source.trim()}
                                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-black text-xs rounded-xl shadow-lg transition-all flex items-center gap-2"
                            >
                                <CheckCircle size={16} /> Save Track
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const FeatureToggle = ({ label, val, onToggle }: { label: string, val: boolean, onToggle: () => void }) => (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 group transition-all hover:bg-white hover:border-indigo-100 hover:shadow-sm">
        <span className="text-[10px] font-black text-slate-700 tracking-tight">{label}</span>
        <button onClick={onToggle} className={`relative w-10 h-5 rounded-full transition-all ${val ? 'bg-indigo-600' : 'bg-slate-200'}`}>
            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform transform ${val ? 'translate-x-5' : 'translate-x-0'} shadow-sm`} />
        </button>
    </div>
);

const IntervalInput = ({ label, val, onChange }: { label: string, val: number, onChange: (v: number) => void }) => (
    <div className="space-y-1">
        <label className="text-[8px] font-black text-slate-400 px-1">{label}</label>
        <input 
            type="number" value={val} 
            onChange={e => onChange(parseInt(e.target.value) || 0)}
            className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl font-black text-xs text-indigo-600 shadow-inner"
        />
    </div>
);

const ToggleLeft = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor" fillOpacity="0.1"/><circle cx="12" cy="12" r="8" fill="white" shadow-lg="true"/></svg>
);

const ToggleRight = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor"/><circle cx="36" cy="12" r="8" fill="white"/></svg>
);